import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useInView } from "framer-motion";
import axios from "axios";
import "@/App.css";
import {
  Phone, Mail, MapPin, Clock, Menu, X, ChevronDown, Star,
  ArrowRight, ArrowUpRight, Play, Instagram, Send, Sparkles,
  Grid3X3, TreeDeciduous, PanelTop, Layers, Zap, Shield, Droplets,
  Sun, Brush, Eye, ExternalLink, ChevronLeft, ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ CONFIGURATION ============
const SITE_CONFIG = {
  name: "CSD3DWALLS",
  tagline: "Creating WOW Factors",
  phone: "305-904-8627",
  email: "shopcoastalsouldesigns@gmail.com",
  address: "13171 NW 42, Ave Opa Locka, FL. 33054",
  instagram: "https://www.instagram.com/csd3dwalls/",
  logo: "https://customer-assets.emergentagent.com/job_d4914d03-57eb-4299-8205-6e817082fa3e/artifacts/kp6wzonp_Csd3d-vector-traced-01-e1724829957256.png",
  heroVideo: "https://customer-assets.emergentagent.com/job_wow-walls/artifacts/wxeqj96v_1.mp4",
  serviceAreas: ["Miami Dade", "Broward County", "Palm Beach", "Monroe", "Lehigh Acres", "South Florida"]
};

// Artificial Greenery specific images
const GREENERY_IMAGES = [
  { id: 1, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/db11ij2m_Photo%20Apr%2018%202025%2C%2012%2025%2050%20PM.jpg", title: "Close-Up Greenery", desc: "Lush ivy wall detail" },
  { id: 2, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/9m8q195g_Photo%20Apr%2024%202025%2C%209%2040%2022%20AM.jpg", title: "Outdoor Lounge", desc: "Private patio with greenery walls" },
  { id: 3, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/d0hhlx42_Photo%20Apr%2029%202025%2C%208%2051%2003%20AM.jpg", title: "Modern Lobby", desc: "Commercial greenery installation" },
  { id: 4, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/3lu81rj2_Photo%20Aug%2004%202025%2C%201%2046%2006%20PM.jpg", title: "Golf Area Privacy", desc: "Mixed slat and greenery wall" },
  { id: 5, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/8i1osay8_Photo%20Feb%2004%202025%2C%2010%2047%2043%20AM.jpg", title: "Poolside Feature", desc: "Full greenery wall installation" },
  { id: 6, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/zgxqhigo_Photo%20Jul%2010%202024%2C%209%2042%2008%20AM.jpg", title: "Commercial Exterior", desc: "Modern building greenery accent" },
  { id: 7, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/fdrfie0t_Photo%20Jun%2003%202025%2C%2010%2045%2029%20AM.jpg", title: "Patio Living Wall", desc: "Tropical greenery with wood ceiling" },
  { id: 8, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/5vx59x0j_Photo%20Jun%2006%202025%2C%2011%2041%2020%20AM.jpg", title: "Privacy Fence", desc: "Full fence greenery coverage" },
  { id: 9, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/vh5o5kg9_Photo%20Jun%2006%202025%2C%2011%2041%2037%20AM.jpg", title: "Pool Privacy Wall", desc: "Backyard pool greenery fence" },
  { id: 10, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/8qvnyv6r_Photo%20Mar%2011%202025%2C%2010%2040%2009%20AM.jpg", title: "Wood Slat Combo", desc: "Greenery with wood slat accents" },
  { id: 11, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/hyil7rmf_Photo%20Feb%2004%202025%2C%2012%2053%2050%20PM.jpg", title: "Patio Transformation", desc: "Full backyard greenery installation" },
  { id: 12, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/nkae73an_Photo%20Jan%2017%202025%2C%201%2004%2058%20PM.jpg", title: "Pool Wall Feature", desc: "Poolside greenery wall with slats" },
  { id: 13, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/ao6fochw_Photo%20Jan%2021%202025%2C%2012%2013%2030%20PM.jpg", title: "Pool House Accent", desc: "Covered area greenery design" },
  { id: 14, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/npkrditf_Photo%20Jul%2010%202024%2C%209%2042%2008%20AM.jpg", title: "Commercial Building", desc: "Modern exterior greenery feature" },
  { id: 15, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/08scb0vy_Photo%20Jun%2003%202025%2C%2010%2045%2029%20AM.jpg", title: "Patio with Ceiling", desc: "Greenery wall with wood ceiling" },
  { id: 16, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/yfd9qjia_Photo%20Jun%2006%202025%2C%2011%2041%2020%20AM.jpg", title: "Fence Privacy", desc: "Long fence greenery installation" },
  { id: 17, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/1q9jop48_Photo%20Jun%2006%202025%2C%2011%2041%2037%20AM.jpg", title: "Pool Fence Wall", desc: "Pool area greenery fence" },
  { id: 18, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/o2a93h0e_Photo%20Mar%2011%202025%2C%2010%2040%2009%20AM.jpg", title: "Wood Post Design", desc: "Greenery with wood post accents" },
  { id: 19, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/vvuq8cfh_Photo%20Sep%2007%202022%2C%208%2051%2023%20PM.jpg", title: "Full Entryway", desc: "Complete entryway greenery wrap" },
];

// 3D Walls specific images
const WALLS_3D_IMAGES = [
  { id: 1, url: "https://customer-assets.emergentagent.com/job_676f8c29-06fd-4c4b-adc4-18e4fb0a2ab2/artifacts/jooqp2jb_Photo%20Dec%2006%202022%2C%208%2033%2047%20AM.jpg", title: "Modern Living Space", desc: "Navy blue 3D textured wall" },
  { id: 2, url: "https://customer-assets.emergentagent.com/job_676f8c29-06fd-4c4b-adc4-18e4fb0a2ab2/artifacts/g0q9e0ku_Photo%20Jan%2016%202026%2C%2012%2004%2052%20PM.jpg", title: "Staircase Feature", desc: "Geometric pattern design" },
  { id: 3, url: "https://customer-assets.emergentagent.com/job_676f8c29-06fd-4c4b-adc4-18e4fb0a2ab2/artifacts/i21pwfs4_Photo%20Jul%2005%202023%2C%203%2012%2023%20PM.jpg", title: "Diamond Pattern", desc: "Silver geometric 3D panels" },
  { id: 4, url: "https://customer-assets.emergentagent.com/job_676f8c29-06fd-4c4b-adc4-18e4fb0a2ab2/artifacts/a85ymvli_Photo%20Jul%2011%202023%2C%202%2042%2024%20PM.jpg", title: "Linear Design", desc: "Black textured hallway wall" },
  { id: 5, url: "https://customer-assets.emergentagent.com/job_676f8c29-06fd-4c4b-adc4-18e4fb0a2ab2/artifacts/1qkxojf4_Photo%20Jul%2019%202025%2C%2012%2045%2047%20PM.jpg", title: "Brick Pattern", desc: "Dark woven texture wall" },
  { id: 6, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/4k5abouv_Photo%20Mar%2005%202025%2C%2011%2019%2001%20AM.jpg", title: "Abstract Pattern", desc: "White textured feature wall" },
  { id: 7, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/j1lbzpht_Photo%20Mar%2028%202023%2C%208%2019%2034%20AM.jpg", title: "Geometric Grid", desc: "Black maze pattern 3D wall" },
  { id: 8, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/ddcz9ift_Photo%20May%2024%202022%2C%209%2005%2026%20AM.jpg", title: "Star Pattern", desc: "Black star geometric wall" },
];

// Exterior PVC Wall Cladding specific images
const PVC_EXTERIOR_IMAGES = [
  { id: 1, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/czgkmvq6_Photo%20Aug%2003%202023%2C%201%2037%2041%20PM.jpg", title: "Exterior PVC Cladding", desc: "Modern exterior wall cladding" },
  { id: 2, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/6ydxrns1_Photo%20Aug%2015%202023%2C%2011%2029%2047%20AM.jpg", title: "Exterior Wall Panels", desc: "Premium PVC exterior finish" },
  { id: 3, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/1akz2oag_Photo%20Feb%2003%202025%2C%202%2016%2001%20PM.jpg", title: "Outdoor Wall Cladding", desc: "Weather-resistant exterior panels" },
  { id: 4, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/1yastz96_Photo%20Jan%2017%202025%2C%206%2030%2032%20PM.jpg", title: "Exterior Feature Wall", desc: "Durable outdoor cladding" },
  { id: 5, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/h7j88wrs_Photo%20Jul%2024%202025%2C%2010%2009%2038%20AM.jpg", title: "Outdoor PVC Panels", desc: "UV-resistant wall panels" },
  { id: 6, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/lc2z65lf_Photo%20Mar%2026%202025%2C%2011%2055%2035%20AM.jpg", title: "Exterior Slat Wall", desc: "Modern exterior slat design" },
  { id: 7, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/h2gei325_Photo%20Nov%2017%202025%2C%202%2019%2012%20PM.jpg", title: "Exterior Column Wrap", desc: "PVC column cladding" },
  { id: 8, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/85r79yvw_Photo%20Nov%2018%202025%2C%2012%2017%2003%20PM.jpg", title: "Outdoor Feature Wall", desc: "Exterior accent wall" },
  { id: 9, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/lujxc5ug_Photo%20Oct%2001%202024%2C%202%2003%2001%20PM.jpg", title: "Exterior Entrance", desc: "PVC entrance cladding" },
  { id: 10, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/y8yk181n_Photo%20Sep%2004%202025%2C%202%2032%2030%20PM.jpg", title: "Covered Patio Walls", desc: "Outdoor living space cladding" },
  { id: 11, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/b2ek1dff_Photo%20Jun%2019%202025%2C%209%2049%2059%20AM.jpg", title: "Exterior Fence Cladding", desc: "Modern fence panel design" },
  { id: 12, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/f4uhlt5o_Photo%20Mar%2007%202025%2C%2012%2041%2024%20PM.jpg", title: "Outdoor Kitchen Area", desc: "Exterior kitchen cladding" },
  { id: 13, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/ba7i4b0m_Photo%20Mar%2007%202025%2C%2012%2042%2039%20PM.jpg", title: "Patio Wall Panels", desc: "Covered patio wall finish" },
  { id: 14, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/yb0qvapt_Photo%20Sep%2012%202025%2C%2011%2042%2046%20AM.jpg", title: "Exterior Building Wrap", desc: "Commercial exterior cladding" },
  { id: 15, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/6rlxkr3j_Photo%20Sep%2019%202025%2C%201%2012%2024%20PM.jpg", title: "Outdoor Structure", desc: "Exterior structure cladding" },
];

// LED Signs specific images
const LED_SIGNS_IMAGES = [
  { id: 1, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/he2n5xa9_Photo%20Apr%2018%202025%2C%2012%2026%2007%20PM.jpg", title: "Los Couple Sign", desc: "Custom script neon on greenery" },
  { id: 2, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/btsle8g0_Photo%20Dec%2005%202022%2C%209%2000%2036%20PM.jpg", title: "Vu New River", desc: "Blue neon lobby sign" },
  { id: 3, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/rflsyarh_Photo%20Dec%2030%202022%2C%203%2027%2011%20PM.jpg", title: "Casa Hernandez", desc: "Warm white family name sign" },
  { id: 4, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/jg2e1zc3_Photo%20Jan%2010%202023%2C%2011%2059%2028%20AM.jpg", title: "Family First", desc: "Outdoor patio LED sign" },
  { id: 5, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/rby1q8h8_Photo%20Jan%2016%202026%2C%2012%2029%2012%20PM%20%282%29.jpg", title: "Rosé All Day", desc: "Orange script on greenery wall" },
  { id: 6, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/2rh06tfm_Photo%20Jan%2016%202026%2C%2012%2029%2012%20PM.jpg", title: "Legends Live Forever", desc: "Red neon tribute sign" },
  { id: 7, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/gl1160v3_Photo%20Jan%2016%202026%2C%2012%2029%2013%20PM.jpg", title: "ClearlyDerm", desc: "White business sign on greenery" },
  { id: 8, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/2y99adwm_Photo%20Jan%2017%202025%2C%206%2032%2009%20PM.jpg", title: "Good Vibes Only", desc: "Teal neon on greenery wall" },
  { id: 9, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/20ccys0r_Photo%20Jul%2021%202022%2C%209%2015%2001%20AM.jpg", title: "Vu New River Lobby", desc: "Blue neon lobby feature" },
  { id: 10, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/9qtcvr2n_Photo%20Jan%2016%202026%2C%2012%2029%2012%20PM%20%281%29.jpg", title: "Mermaids On Duty", desc: "Teal script on greenery wall" },
  { id: 11, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/voisurr0_Photo%20Jun%2003%202025%2C%2010%2047%2021%20AM.jpg", title: "Pura Vida", desc: "White neon on ivy wall" },
];

// Wainscoting specific images
const WAINSCOTING_IMAGES = [
  { id: 1, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/jc1982a1_Photo%20Apr%2018%202023%2C%202%2001%2041%20PM.jpg", title: "Gray Entryway Wainscoting", desc: "Elegant entryway feature wall" },
  { id: 2, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/pokld1rx_Photo%20Apr%2018%202025%2C%2012%2024%2022%20PM.jpg", title: "Grand Foyer Panels", desc: "High ceiling wainscoting" },
  { id: 3, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/g5u6tomm_Photo%20Apr%2018%202025%2C%2012%2024%2047%20PM.jpg", title: "Staircase Feature", desc: "Full height wall panels" },
  { id: 4, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/gt71qaqd_Photo%20Apr%2018%202025%2C%2012%2024%2051%20PM.jpg", title: "Classic White Panels", desc: "Traditional wainscoting design" },
  { id: 5, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/ifpqykw6_Photo%20Apr%2018%202025%2C%2012%2024%2058%20PM.jpg", title: "Two-Story Installation", desc: "Grand staircase wainscoting" },
  { id: 6, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/fc0661qo_Photo%20Apr%2018%202025%2C%2012%2025%2010%20PM.jpg", title: "Modern Panel Design", desc: "Contemporary wainscoting style" },
  { id: 7, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/z2nlxp9w_Photo%20Apr%2027%202023%2C%207%2026%2034%20PM.jpg", title: "Hallway Wainscoting", desc: "Elegant hallway transformation" },
  { id: 8, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/vw1nx9ib_Photo%20Aug%2029%202023%2C%2012%2047%2037%20PM.jpg", title: "Custom Panel Work", desc: "Premium wainscoting installation" },
];

// Real Wood Slat Walls specific images
const WOOD_SLAT_IMAGES = [
  { id: 1, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/v3i6jze6_Photo%20Apr%2027%202023%2C%204%2035%2050%20PM.jpg", title: "TV Feature Wall", desc: "Modern wood slat media wall" },
  { id: 2, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/yrh55new_Photo%20Aug%2016%202023%2C%201%2008%2016%20PM.jpg", title: "Living Room Accent", desc: "Wood slat column design" },
  { id: 3, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/sot0b4w3_Photo%20Jan%2016%202026%2C%2012%2026%2002%20PM.jpg", title: "Bohemian Style", desc: "Decorative slat accents" },
  { id: 4, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/l3vp0fvv_Photo%20Jul%2026%202024%2C%203%2028%2020%20PM.jpg", title: "Commercial Entryway", desc: "Full wall slat installation" },
  { id: 5, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/jb65nf1n_Photo%20Jul%2026%202024%2C%203%2029%2047%20PM.jpg", title: "Hallway Design", desc: "Floor to ceiling slat walls" },
  { id: 6, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/x73td2y0_Photo%20Mar%2014%202023%2C%201%2025%2048%20PM.jpg", title: "Staircase Feature", desc: "Natural wood slat stairwell" },
];

// Exterior Roof Cladding specific images
const ROOF_CLADDING_IMAGES = [
  { id: 1, url: "https://customer-assets.emergentagent.com/job_0b841d3f-d92b-4774-b6d6-d13b30e7ae6c/artifacts/bfuz1rbk_nn0dj4gd_Photo%20Jun%2003%202025%2C%2010%2043%2027%20AM.jpg", title: "Patio Ceiling with Greenery", desc: "Wood grain roof cladding with greenery wall" },
  { id: 2, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/llp296na_Photo%20Jul%2002%202025%2C%203%2050%2011%20PM.jpg", title: "Carport Ceiling", desc: "White PVC roof cladding" },
  { id: 3, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/mb3p01j7_Photo%20Jul%2028%202025%2C%203%2003%2030%20PM.jpg", title: "Covered Patio", desc: "Modern roof panel installation" },
  { id: 4, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/nn0dj4gd_Photo%20Jun%2003%202025%2C%2010%2043%2027%20AM.jpg", title: "Pool Area Cover", desc: "Seamless roof cladding" },
  { id: 5, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/ds2olulc_Photo%20Nov%2012%202025%2C%2011%2020%2051%20AM.jpg", title: "Outdoor Living", desc: "White ceiling panels" },
  { id: 6, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/0lbf4577_Photo%20Apr%2018%202025%2C%2012%2026%2013%20PM.jpg", title: "Patio Roof Cladding", desc: "Modern PVC roof installation" },
  { id: 7, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/um1fysbp_Photo%20Aug%2002%202025%2C%202%2026%2030%20PM.jpg", title: "Outdoor Ceiling", desc: "Premium roof panel finish" },
  { id: 8, url: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/spdjxpxn_Photo%20Dec%2021%202024%2C%2012%2007%2029%20PM.jpg", title: "Covered Area", desc: "Wood-grain ceiling panels" },
];

// PVC Interior Wall Cladding specific images
const PVC_INTERIOR_IMAGES = [
  { id: 1, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/unkg63xq_Photo%20Apr%2007%202023%2C%204%2046%2050%20PM.jpg", title: "Modern Dining Area", desc: "Gray slat wall with mirror accent" },
  { id: 2, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/9lnmx03p_Photo%20Dec%2015%202024%2C%204%2034%2004%20PM.jpg", title: "Entertainment Wall", desc: "Full wall slat with LED shelves" },
  { id: 3, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/5692c5si_Photo%20Dec%2015%202025%2C%2011%2042%2053%20AM.jpg", title: "Home Office", desc: "Walnut finish office backdrop" },
  { id: 4, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/b2epiu5o_Photo%20Jan%2013%202026%2C%202%2028%2032%20PM.jpg", title: "Commercial Hallway", desc: "Brown slat wall corridor" },
  { id: 5, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/l58hxyej_Photo%20Jan%2016%202026%2C%2012%2008%2016%20PM.jpg", title: "Fireplace Feature", desc: "Gray slat media wall with fireplace" },
  { id: 6, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/rrtp7p3g_Photo%20May%2013%202024%2C%2012%2016%2044%20PM.jpg", title: "Reception Wall", desc: "Dark slat wall with seating area" },
  { id: 7, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/e0u3ehv5_Photo%20May%2027%202025%2C%2012%2049%2027%20PM.jpg", title: "Living Room TV Wall", desc: "Natural wood slat media wall" },
  { id: 8, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/4zd9x54w_Photo%20Nov%2008%202025%2C%2010%2037%2019%20AM.jpg", title: "Two-Story Stairwell", desc: "Full height slat feature wall" },
  { id: 9, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/xyvrqhnp_Photo%20Oct%2002%202025%2C%207%2006%2038%20PM.jpg", title: "Elegant Dining Room", desc: "Brown slat accent wall" },
  { id: 10, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/znd4b5np_Photo%20Sep%2029%202025%2C%2010%2021%2041%20AM.jpg", title: "Contemporary Entryway", desc: "Dark slat wall with LED accents" },
  { id: 11, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/7bwoguh3_Photo%20Jul%2008%202025%2C%2012%2043%2048%20PM.jpg", title: "Entryway Console", desc: "Natural wood slat accent wall" },
  { id: 12, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/sdxanm7l_Photo%20Jul%2018%202025%2C%203%2048%2054%20PM.jpg", title: "Master Bedroom", desc: "Cream slat with 3D accent panels" },
  { id: 13, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/z4ddd03u_Photo%20Jul%2019%202025%2C%2012%2048%2035%20PM.jpg", title: "Elevator Interior", desc: "Full wrap slat wall installation" },
  { id: 14, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/kw4svud6_Photo%20Jun%2017%202024%2C%201%2027%2050%20PM.jpg", title: "Media Room", desc: "Black slat wall with fireplace" },
  { id: 15, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/7os1ufdy_Photo%20Mar%2013%202025%2C%2010%2004%2015%20AM.jpg", title: "Staircase Landing", desc: "Bronze horizontal slat feature" },
];

// Exterior Tongue & Groove specific images
const TONGUE_GROOVE_IMAGES = [
  { id: 1, url: "https://customer-assets.emergentagent.com/job_five-step-process/artifacts/ai7laaz2_quadz5qp_Photo%20Oct%2029%202025%2C%2010%2056%2023%20AM.jpg", title: "Carport Ceiling", desc: "White T&G overhang installation" },
  { id: 2, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/7jmqx8ig_Photo%20Dec%2009%202025%2C%202%2012%2021%20PM.jpg", title: "Patio Ceiling", desc: "Brown tongue & groove overhang" },
  { id: 3, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/rc7atgzn_Photo%20Dec%2012%202025%2C%202%2053%2056%20PM.jpg", title: "Covered Entryway", desc: "Dark walnut ceiling panels" },
  { id: 4, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/xe13k2yl_Photo%20Dec%2012%202025%2C%202%2054%2035%20PM.jpg", title: "Multi-Level Design", desc: "Exterior ceiling installation" },
  { id: 5, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/20ljij1p_Photo%20Jul%2004%202025%2C%201%2003%2017%20PM.jpg", title: "Modern Soffit", desc: "Dark wood exterior soffit" },
  { id: 6, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/ftjtljh7_Photo%20Jul%2024%202025%2C%2010%2008%2013%20AM.jpg", title: "Full Exterior", desc: "Wall and ceiling cladding" },
  { id: 7, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/jk8i8xf6_Photo%20Sep%2011%202025%2C%2011%2017%2028%20AM.jpg", title: "Poolside Patio", desc: "Wood grain ceiling panels" },
  { id: 8, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/y6pqd0u4_Photo%20Sep%2011%202025%2C%2011%2022%2030%20AM.jpg", title: "Extended Patio Cover", desc: "Full patio ceiling installation" },
  { id: 9, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/5ghlekz9_Photo%20Jul%2024%202025%2C%2010%2008%2057%20AM.jpg", title: "Wood Grain Patio", desc: "Brown exterior ceiling" },
  { id: 10, url: "https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/ulh0isr6_Photo%20Oct%2029%202025%2C%2010%2056%2038%20AM.jpg", title: "White Soffit", desc: "Clean white ceiling panels" },
];

// Gallery with categories - includes ALL images from all services
const GALLERY_IMAGES = [
  // 3D Walls - All images
  ...WALLS_3D_IMAGES.map((img, i) => ({ id: `3d-${i+1}`, url: img.url, category: "3d-walls", title: img.title, desc: img.desc })),
  // Wainscoting - All images
  ...WAINSCOTING_IMAGES.map((img, i) => ({ id: `wain-${i+1}`, url: img.url, category: "wainscoting", title: img.title, desc: img.desc })),
  // PVC Interior - All images
  ...PVC_INTERIOR_IMAGES.map((img, i) => ({ id: `pvc-${i+1}`, url: img.url, category: "pvc-interior", title: img.title, desc: img.desc })),
  // PVC Exterior - All images
  ...PVC_EXTERIOR_IMAGES.map((img, i) => ({ id: `pvc-ext-${i+1}`, url: img.url, category: "pvc-exterior", title: img.title, desc: img.desc })),
  // Wood Slats - All images
  ...WOOD_SLAT_IMAGES.map((img, i) => ({ id: `wood-${i+1}`, url: img.url, category: "wood-slats", title: img.title, desc: img.desc })),
  // Tongue & Groove - All images
  ...TONGUE_GROOVE_IMAGES.map((img, i) => ({ id: `tg-${i+1}`, url: img.url, category: "tongue-groove", title: img.title, desc: img.desc })),
  // Roof Cladding - All images
  ...ROOF_CLADDING_IMAGES.map((img, i) => ({ id: `roof-${i+1}`, url: img.url, category: "roof-cladding", title: img.title, desc: img.desc })),
  // Artificial Greenery - All images
  ...GREENERY_IMAGES.map((img, i) => ({ id: `green-${i+1}`, url: img.url, category: "artificial-greenery", title: img.title, desc: img.desc })),
  // LED Signs - All images
  ...LED_SIGNS_IMAGES.map((img, i) => ({ id: `led-${i+1}`, url: img.url, category: "led-signs", title: img.title, desc: img.desc })),
];

const REVIEWS = [
  { name: "Amanda Nunez", text: "We did an accent wall and they were efficient, neat and super professional! We are looking to book another one in the future!", rating: 5 },
  { name: "David Dejesus", text: "I have used Coastal Soul Designs 3D Walls and Artificial Ivy at least 3x already for different projects around the house. They are an amazing company and very detail oriented.", rating: 5 },
  { name: "Caroline Benitez", text: "Manny and his team were great. Not only did they deliver on their promise to give us a wow factor but they were detail oriented and careful.", rating: 5 },
  { name: "Jeremy Katz", text: "The entire experience working with Coastal Soul Designs has been awesome. Very professional, affordable and the finished results look great.", rating: 5 },
];

const SERVICES = [
  { id: "pvc-interior", name: "Interior PVC Wall Cladding", icon: Layers, slug: "/pvc-interior-wall-cladding", image: PVC_INTERIOR_IMAGES[0].url },
  { id: "pvc-exterior", name: "Exterior PVC Wall Cladding", icon: Layers, slug: "/exterior-pvc-wall-cladding", image: PVC_EXTERIOR_IMAGES[0].url },
  { id: "tongue-groove", name: "Exterior Tongue & Groove", icon: PanelTop, slug: "/exterior-tongue-groove-roof-soffit", image: TONGUE_GROOVE_IMAGES[0].url },
  { id: "roof-cladding", name: "Exterior Roof Cladding", icon: PanelTop, slug: "/exterior-roof-cladding", image: ROOF_CLADDING_IMAGES[0].url },
  { id: "3d-walls", name: "3D Walls", icon: Grid3X3, slug: "/3d-walls", image: WALLS_3D_IMAGES[0].url },
  { id: "artificial-greenery", name: "Artificial Greenery", icon: TreeDeciduous, slug: "/artificial-greenery-walls", image: GREENERY_IMAGES[0].url },
  { id: "wood-slats", name: "Real Wood Slat Walls", icon: PanelTop, slug: "/real-wood-slat-walls", image: WOOD_SLAT_IMAGES[0].url },
  { id: "wainscoting", name: "Wainscoting Walls", icon: Layers, slug: "/wainscoting-walls", image: WAINSCOTING_IMAGES[0].url },
  { id: "led-signs", name: "Custom LED Signs", icon: Zap, slug: "/custom-led-signs", image: LED_SIGNS_IMAGES[1].url },
];

// Before/After Transformation Pairs
const BEFORE_AFTER_PAIRS = [
  {
    id: 1,
    title: "Patio Ceiling Transformation",
    before: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/i9sjzgb4_Photo%20Sep%2003%202025%2C%209%2015%2000%20AM.jpg",
    after: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/j556gm4x_Photo%20Sep%2004%202025%2C%202%2031%2013%20PM.jpg",
    desc: "Plain patio ceiling transformed with beautiful wood-grain tongue & groove panels"
  },
  {
    id: 2,
    title: "Exterior Column & Ceiling Upgrade",
    before: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/gdsupgma_Photo%20Jan%2016%202026%2C%2012%2038%2043%20PM.jpg",
    after: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/ligs97dh_Photo%20Oct%2029%202025%2C%2010%2054%2040%20AM.jpg",
    desc: "Exterior columns and ceiling transformed with premium PVC cladding"
  },
  {
    id: 3,
    title: "Staircase Wood Slat Wall",
    before: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/xzk4xw53_1b.jpg",
    after: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/mculo5t2_1.jpg",
    desc: "Plain staircase wall transformed with elegant wood slat panels"
  },
  {
    id: 4,
    title: "Feature Wall Transformation",
    before: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/1smwljzl_2b.jpg",
    after: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/g9qq77s8_2.jpg",
    desc: "Blank wall transformed with stunning accent wall design"
  },
  {
    id: 5,
    title: "Home Office Feature Wall",
    before: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/7zb9y0je_Photo%20Dec%2015%202025%2C%2011%2014%2031%20AM.jpg",
    after: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/nmpva8zh_Photo%20Dec%2015%202025%2C%2011%2044%2032%20AM.jpg",
    desc: "Blank office corner transformed with dark wood slat accent wall"
  },
  {
    id: 6,
    title: "Dining Room Slat Wall",
    before: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/e628z9aq_Photo%20Jan%2016%202026%2C%2012%2044%2045%20PM.jpg",
    after: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/2t8ojily_Photo%20Jan%2016%202026%2C%2012%2044%2042%20PM.jpg",
    desc: "Plain dining room wall transformed with elegant gray slat panels"
  },
  {
    id: 7,
    title: "Staircase Accent Wall",
    before: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/rzf1wxzs_Photo%20Nov%2005%202025%2C%208%2059%2053%20AM.jpg",
    after: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/vypcuml9_Photo%20Nov%2008%202025%2C%2010%2039%2037%20AM.jpg",
    desc: "Damaged staircase wall transformed with natural wood slat panels"
  },
  {
    id: 8,
    title: "Living Room Feature Wall",
    before: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/y2x7ovoh_Photo%20Nov%2017%202025%2C%209%2059%2009%20AM.jpg",
    after: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/hou456kq_Photo%20Nov%2018%202025%2C%2012%2024%2049%20PM.jpg",
    desc: "Plain wall transformed with modern PVC slat accent wall"
  },
  {
    id: 9,
    title: "Commercial Hallway Upgrade",
    before: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/kc3syiy3_Photo%20Oct%2020%202025%2C%2010%2005%2042%20AM.jpg",
    after: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/gptfqkp9_Photo%20Oct%2020%202025%2C%204%2036%2007%20PM.jpg",
    desc: "Commercial corridor transformed with decorative damask pattern wall"
  },
  {
    id: 10,
    title: "Exterior Patio Transformation",
    before: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/b17luxwt_Photo%20Nov%2018%202025%2C%203%2006%2008%20PM.jpg",
    after: "https://customer-assets.emergentagent.com/job_homepage-fix-5/artifacts/kvjxxg4g_Photo%20Dec%2002%202025%2C%201%2002%2038%20PM.jpg",
    desc: "Outdoor patio space transformed with premium PVC cladding"
  },
  {
    id: 11,
    title: "Balcony Ceiling Transformation",
    before: "https://customer-assets.emergentagent.com/job_before-after-suite/artifacts/ob3nmbsa_Photo%20Sep%2008%202025%2C%2010%2015%2050%20AM.jpg",
    after: "https://customer-assets.emergentagent.com/job_before-after-suite/artifacts/wlcba4x2_Photo%20Sep%2011%202025%2C%2011%2018%2020%20AM.jpg",
    desc: "Plain white balcony ceiling with exposed wires transformed with elegant dark wood tongue & groove panels"
  },
  {
    id: 12,
    title: "Balcony Overhang Upgrade",
    before: "https://customer-assets.emergentagent.com/job_before-after-suite/artifacts/wfgqo8nn_Photo%20Jan%2016%202026%2C%2012%2044%2036%20PM%202.15.03%E2%80%AFPM.jpg",
    after: "https://customer-assets.emergentagent.com/job_before-after-suite/artifacts/f0xmseky_Photo%20Dec%2012%202025%2C%202%2054%2035%20PM%202.15.03%E2%80%AFPM.jpg",
    desc: "Multi-level balcony ceiling transformed with premium dark wood paneling"
  },
  {
    id: 13,
    title: "Garage Exterior Upgrade",
    before: "https://customer-assets.emergentagent.com/job_before-after-suite/artifacts/xgdnckqf_j9u2pwdo_Photo%20Jan%2016%202026%2C%2012%2040%2058%20PM.jpg",
    after: "https://customer-assets.emergentagent.com/job_before-after-suite/artifacts/dl05z6p2_rgbkx0xk_Photo%20Aug%2022%202025%2C%201%2027%2026%20PM-1.jpg",
    desc: "Plain white garage exterior transformed with stunning dark wood slat siding"
  },
];

// ============ CUSTOM CURSOR COMPONENT (DISABLED - Using normal cursor with gradient styling) ============
const CustomCursor = () => {
  // Cursor is now set to normal via CSS
  // This component is kept for potential future use but returns null
  return null;
};

// ============ MAGNETIC BUTTON ============
const MagneticButton = ({ children, className = "", ...props }) => {
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  
  const handleMouse = (e) => {
    const { clientX, clientY } = e;
    const { left, top, width, height } = buttonRef.current.getBoundingClientRect();
    const x = (clientX - left - width / 2) * 0.3;
    const y = (clientY - top - height / 2) * 0.3;
    setPosition({ x, y });
  };
  
  const handleMouseLeave = () => setPosition({ x: 0, y: 0 });
  
  return (
    <motion.button
      ref={buttonRef}
      className={`magnetic-btn ${className}`}
      onMouseMove={handleMouse}
      onMouseLeave={handleMouseLeave}
      animate={{ x: position.x, y: position.y }}
      transition={{ type: "spring", stiffness: 150, damping: 15 }}
      {...props}
    >
      {children}
    </motion.button>
  );
};

// ============ ANIMATED SECTION ============
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.4, 0, 0.2, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============ BEFORE/AFTER SLIDER ============
const BeforeAfterSlider = ({ before, after }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  
  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = (x / rect.width) * 100;
    setSliderPosition(percentage);
  }, []);
  
  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleTouchMove = (e) => handleMove(e.touches[0].clientX);
  
  useEffect(() => {
    const handleMouseMove = (e) => { if (isDragging.current) handleMove(e.clientX); };
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [handleMove]);
  
  return (
    <div
      ref={containerRef}
      className="before-after-container relative aspect-video cursor-ew-resize"
      onTouchMove={handleTouchMove}
    >
      {/* After Image (Background) */}
      <img src={after} alt="After" className="absolute inset-0 w-full h-full object-cover" />
      
      {/* Before Image (Clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img src={before} alt="Before" className="absolute inset-0 w-full h-full object-cover" />
      </div>
      
      {/* Slider Handle */}
      <div
        className="before-after-slider"
        style={{ left: `${sliderPosition}%` }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      />
      
      {/* Labels */}
      <div className="absolute top-4 left-4 px-3 py-1 bg-black/70 rounded text-xs font-semibold uppercase tracking-wider">Before</div>
      <div className="absolute top-4 right-4 px-3 py-1 bg-orange-500/90 rounded text-xs font-semibold uppercase tracking-wider text-black">After</div>
    </div>
  );
};

// ============ SEO COMPONENT ============
const SEO = ({ title, description, schema, image }) => {
  const pageTitle = title ? `${title} | csd3dwalls.com` : "csd3dwalls.com - #1 3D Wall Installation Miami";
  const pageDescription = description || "Experience premium 3D walls, ivy installations, and custom wall designs in Miami. CSD3DWALLS creates the ultimate WOW factor.";
  const pageImage = image || "https://customer-assets.emergentagent.com/job_0b841d3f-d92b-4774-b6d6-d13b30e7ae6c/artifacts/bfuz1rbk_nn0dj4gd_Photo%20Jun%2003%202025%2C%2010%2043%2027%20AM.jpg";
  
  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      
      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="CSD3DWALLS" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`https://csd3dwalls.com${window.location.pathname}`} />
      
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Helmet>
  );
};

// ============ HEADER ============
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  const closeMenus = () => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  };
  
  const handleNavClick = () => {
    closeMenus();
    window.scrollTo(0, 0);
  };
  
  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Portfolio", path: "/transformations" },
    { name: "Our Process", path: "/our-process" },
    { name: "Shop", path: "/shop" },
    { name: "Contact", path: "/contact" },
  ];
  
  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        isHomePage ? "top-12" : "top-0"
      } ${
        isScrolled ? "glass py-3" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" onClick={handleNavClick} className="relative z-10" data-cursor="HOME">
            <motion.img
              src={SITE_CONFIG.logo}
              alt="CSD3DWALLS"
              className="h-12 lg:h-14 w-auto"
              whileHover={{ scale: 1.05 }}
            />
          </Link>
          
          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.slice(0, 2).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleNavClick}
                className={`nav-link ${location.pathname === link.path ? "active" : ""}`}
                data-cursor="VIEW"
              >
                {link.name}
              </Link>
            ))}
            
            {/* Our Work Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button className="nav-link flex items-center gap-1" data-cursor="EXPLORE">
                Our Work
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isServicesOpen ? "rotate-180" : ""}`} />
              </button>
              
              <AnimatePresence>
                {isServicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-72 glass rounded-2xl p-4 grid grid-cols-2 gap-2"
                  >
                    {SERVICES.map((service) => (
                      <Link
                        key={service.slug}
                        to={service.slug}
                        onClick={handleNavClick}
                        className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors group"
                        data-cursor="VIEW"
                      >
                        <service.icon className="w-4 h-4 text-orange-500 group-hover:text-teal-400 transition-colors" />
                        <span className="text-sm text-white/80 group-hover:text-white">{service.name}</span>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {navLinks.slice(2).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleNavClick}
                className={`nav-link ${location.pathname === link.path ? "active" : ""}`}
                data-cursor="VIEW"
              >
                {link.name}
              </Link>
            ))}
          </nav>
          
          {/* CTA */}
          <div className="hidden lg:block">
            <Link to="/contact" onClick={handleNavClick} data-cursor="GET QUOTE">
              <MagneticButton className="btn-primary text-sm">
                Get Quote
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </MagneticButton>
            </Link>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden relative z-10 p-2"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <motion.span
                animate={{ rotate: isMobileMenuOpen ? 45 : 0, y: isMobileMenuOpen ? 8 : 0 }}
                className="w-full h-0.5 bg-white origin-left"
              />
              <motion.span
                animate={{ opacity: isMobileMenuOpen ? 0 : 1 }}
                className="w-full h-0.5 bg-white"
              />
              <motion.span
                animate={{ rotate: isMobileMenuOpen ? -45 : 0, y: isMobileMenuOpen ? -8 : 0 }}
                className="w-full h-0.5 bg-white origin-left"
              />
            </div>
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "100vh" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden fixed inset-0 top-0 bg-[#0a0a0a] z-40 overflow-y-auto"
          >
            <div className="flex flex-col items-center pt-20 pb-48 gap-4 min-h-full px-4">
              {navLinks.slice(0, 2).map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link
                    to={link.path}
                    onClick={closeMenus}
                    className="text-xl font-bold text-white hover:text-orange-500 transition-colors"
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              
              {/* Our Work Section for Mobile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="w-full"
              >
                <button 
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="text-xl font-bold text-white hover:text-orange-500 transition-colors w-full flex items-center justify-center gap-2"
                >
                  Our Work
                  <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isServicesOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {isServicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-3 space-y-1"
                    >
                      {SERVICES.map((service) => (
                        <Link
                          key={service.slug}
                          to={service.slug}
                          onClick={closeMenus}
                          className="block text-center text-white/70 hover:text-orange-500 transition-colors py-1.5 text-sm"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              
              {navLinks.slice(2).map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (i + 3) * 0.1 }}
                >
                  <Link
                    to={link.path}
                    onClick={closeMenus}
                    className="text-xl font-bold text-white hover:text-orange-500 transition-colors"
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <Link to="/contact" onClick={closeMenus} className="btn-primary mt-2">
                  Get Quote
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

// ============ MOBILE FOOTER ============
const MobileFooter = () => (
  <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden glass border-t border-white/5">
    <div className="flex items-stretch">
      <a
        href={`tel:${SITE_CONFIG.phone}`}
        className="flex-1 flex items-center justify-center gap-2 py-4 bg-orange-500 text-black font-semibold"
      >
        <Phone className="w-4 h-4" /> Call
      </a>
      <Link
        to="/contact"
        className="flex-1 flex items-center justify-center gap-2 py-4 bg-teal-500 text-black font-semibold"
      >
        <Send className="w-4 h-4" /> Quote
      </Link>
      <a
        href={SITE_CONFIG.instagram}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center px-6 py-4 bg-white/5"
      >
        <Instagram className="w-5 h-5" />
      </a>
    </div>
  </div>
);

// ============ FOOTER ============
const Footer = () => (
  <footer className="bg-[#050505] border-t border-white/5 pt-20 pb-32 lg:pb-12">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
        {/* Brand */}
        <div className="lg:col-span-2">
          <img src={SITE_CONFIG.logo} alt="CSD3DWALLS" className="h-16 mb-6" />
          <p className="text-white/50 text-lg leading-relaxed max-w-md mb-6">
            Creating the ultimate WOW factor since 2016. Licensed & Insured. Premium wall transformations across South Florida.
          </p>
          <a
            href={SITE_CONFIG.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 text-white/50 hover:text-orange-500 transition-colors"
            data-cursor="FOLLOW"
          >
            <Instagram className="w-5 h-5" />
            <span>@csd3dwalls</span>
          </a>
        </div>
        
        {/* Our Work */}
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-white/30 mb-6">Our Work</h4>
          <ul className="space-y-3">
            {SERVICES.map((service) => (
              <li key={service.slug}>
                <Link
                  to={service.slug}
                  className="text-white/50 hover:text-white transition-colors"
                  data-cursor="VIEW"
                >
                  {service.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Contact */}
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-white/30 mb-6">Contact</h4>
          <ul className="space-y-4">
            <li>
              <a href={`tel:${SITE_CONFIG.phone}`} className="flex items-center gap-3 text-white/50 hover:text-orange-500 transition-colors">
                <Phone className="w-4 h-4 text-orange-500" />
                {SITE_CONFIG.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${SITE_CONFIG.email}`} className="flex items-center gap-3 text-white/50 hover:text-teal-400 transition-colors text-sm">
                <Mail className="w-4 h-4 text-teal-400" />
                {SITE_CONFIG.email}
              </a>
            </li>
            <li className="flex items-start gap-3 text-white/50 text-sm">
              <MapPin className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
              {SITE_CONFIG.address}
            </li>
          </ul>
        </div>
      </div>
      
      {/* Service Areas */}
      <div className="border-t border-white/5 pt-8 mb-8">
        <h4 className="text-sm font-semibold uppercase tracking-wider text-white/30 mb-4 text-center">Service Areas</h4>
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
          {SITE_CONFIG.serviceAreas.map((area, i) => (
            <span key={i} className="flex items-center gap-2 text-white/50 text-sm">
              <span className="text-teal-400">✓</span> {area}
            </span>
          ))}
        </div>
      </div>
      
      <div className="border-t border-white/5 pt-8">
        <p className="text-white/20 text-sm text-center">
          © {new Date().getFullYear()} CSD3DWALLS. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// ============ BEFORE/AFTER CAROUSEL (HOME PAGE) ============
const BeforeAfterCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % BEFORE_AFTER_PAIRS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);
  
  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };
  
  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + BEFORE_AFTER_PAIRS.length) % BEFORE_AFTER_PAIRS.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };
  
  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % BEFORE_AFTER_PAIRS.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };
  
  const currentPair = BEFORE_AFTER_PAIRS[currentIndex];
  
  return (
    <section className="py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-12">
            <span className="text-sm font-semibold uppercase tracking-widest text-teal-400 mb-4 block">Transformations</span>
            <h2 className="heading-lg text-white mb-6">Before & After</h2>
            <p className="body-lg text-white/50">See the WOW factor in action</p>
          </div>
        </AnimatedSection>
        
        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button 
            onClick={goToPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-20 w-12 h-12 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors"
            aria-label="Previous transformation"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          
          <button 
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-20 w-12 h-12 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors"
            aria-label="Next transformation"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
          
          {/* Current Slide */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
              className="card-premium p-6 md:p-8"
            >
              <h3 className="text-2xl font-bold text-white mb-2 text-center">{currentPair.title}</h3>
              <p className="text-white/50 text-center mb-8">{currentPair.desc}</p>
              
              {/* Side by Side Images */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Before */}
                <div className="relative">
                  <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-black/80 rounded-lg">
                    <span className="text-white text-sm font-bold uppercase">Before</span>
                  </div>
                  <img 
                    src={currentPair.before} 
                    alt={`${currentPair.title} - Before`}
                    className="w-full h-auto rounded-xl"
                  />
                </div>
                
                {/* After */}
                <div className="relative">
                  <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-gradient-to-r from-orange-500 to-teal-500 rounded-lg">
                    <span className="text-white text-sm font-bold uppercase">After</span>
                  </div>
                  <img 
                    src={currentPair.after} 
                    alt={`${currentPair.title} - After`}
                    className="w-full h-auto rounded-xl"
                  />
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          
          {/* Slide Counter */}
          <div className="text-center mt-6 text-white/40 text-sm">
            {currentIndex + 1} / {BEFORE_AFTER_PAIRS.length}
          </div>
          
          {/* Dot Navigation */}
          <div className="flex justify-center gap-2 mt-4 flex-wrap max-w-xl mx-auto">
            {BEFORE_AFTER_PAIRS.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? "bg-gradient-to-r from-orange-500 to-teal-400 w-8" 
                    : "bg-white/20 hover:bg-white/40"
                }`}
                aria-label={`Go to transformation ${index + 1}`}
              />
            ))}
          </div>
        </div>
        
        <AnimatedSection delay={0.2}>
          <div className="text-center mt-12">
            <Link to="/transformations" className="btn-secondary" data-cursor="SEE MORE">
              View All Transformations
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ============ GALLERY SECTION ============
const GallerySection = () => {
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  
  const serviceFilters = [
    { id: "all", name: "All" },
    { id: "3d-walls", name: "3D Walls" },
    { id: "artificial-greenery", name: "Artificial Greenery" },
    { id: "wainscoting", name: "Wainscoting" },
    { id: "pvc-interior", name: "PVC Interior" },
    { id: "pvc-exterior", name: "PVC Exterior" },
    { id: "wood-slats", name: "Wood Slats" },
    { id: "tongue-groove", name: "Tongue & Groove" },
    { id: "roof-cladding", name: "Roof Cladding" },
    { id: "led-signs", name: "LED Signs" },
  ];
  
  const filteredImages = filter === "all"
    ? GALLERY_IMAGES
    : GALLERY_IMAGES.filter(img => img.category === filter);
  
  return (
    <section className="py-32 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background accents */}
      <div className="gradient-blob gradient-blob-orange w-96 h-96 top-20 -left-48" />
      <div className="gradient-blob gradient-blob-teal w-96 h-96 bottom-20 -right-48" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Portfolio</span>
            <h2 className="heading-lg text-white mb-6">Our Work</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-teal-400 mx-auto" />
          </div>
        </AnimatedSection>
        
        {/* Service Filters */}
        <AnimatedSection delay={0.1}>
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {serviceFilters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`filter-btn ${filter === f.id ? "active" : ""}`}
                data-cursor="SELECT"
              >
                {f.name}
              </button>
            ))}
          </div>
        </AnimatedSection>
        
        {/* Masonry Grid - No hover text */}
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
          {filteredImages.map((image, index) => (
            <AnimatedSection key={image.id} delay={index * 0.05}>
              <motion.div
                layout
                className="gallery-item mb-4 break-inside-avoid cursor-pointer"
                onClick={() => setLightbox(image)}
                whileHover={{ scale: 1.02 }}
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-auto rounded-xl"
                  loading="lazy"
                />
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </div>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setLightbox(null)}
          >
            <button
              className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors"
              onClick={() => setLightbox(null)}
            >
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={lightbox.url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// ============ INSTAGRAM FEED ============
const InstagramFeed = () => {
  useEffect(() => {
    // Load SociableKit Instagram feed widget script
    const existing = document.querySelector('script[data-sociablekit="true"]');
    if (!existing) {
      const script = document.createElement("script");
      script.src = "https://widgets.sociablekit.com/instagram-feed/widget.js";
      script.async = true;
      script.defer = true;
      script.setAttribute("data-sociablekit", "true");
      document.body.appendChild(script);
    }
  }, []);

  return (
    <section className="py-24 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <AnimatedSection>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12">
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-teal-400 mb-2 block">Follow Us</span>
              <h2 className="heading-sm text-white">@csd3dwalls</h2>
            </div>
            <a
              href={SITE_CONFIG.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2"
              data-cursor="FOLLOW"
            >
              <Instagram className="w-5 h-5" />
              Follow on Instagram
            </a>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          <div className="sk-instagram-feed" data-embed-id="25690018"></div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ============ REVIEWS SECTION ============
const ReviewsSection = () => {
  const [active, setActive] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => setActive((p) => (p + 1) % REVIEWS.length), 5000);
    return () => clearInterval(timer);
  }, []);
  
  return (
    <section className="py-32 bg-[#0a0a0a] relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-50" />
      
      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <span className="text-sm font-semibold uppercase tracking-widest text-teal-400 mb-4 block">Testimonials</span>
            <h2 className="heading-lg text-white">Client Love</h2>
          </div>
        </AnimatedSection>
        
        <AnimatedSection delay={0.2}>
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <div className="flex justify-center gap-1 mb-8">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-orange-500 text-orange-500" />
                  ))}
                </div>
                <p className="text-2xl md:text-3xl lg:text-4xl font-light text-white leading-relaxed mb-8 text-balance">
                  &ldquo;{REVIEWS[active].text}&rdquo;
                </p>
                <p className="text-orange-500 font-semibold text-lg">{REVIEWS[active].name}</p>
              </motion.div>
            </AnimatePresence>
            
            {/* Navigation */}
            <div className="flex justify-center gap-3 mt-12">
              {REVIEWS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`w-12 h-1 rounded-full transition-all duration-300 ${
                    i === active ? "bg-gradient-to-r from-orange-500 to-teal-400" : "bg-white/20"
                  }`}
                />
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ============ HOME PAGE ============
const HomePage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  
  return (
    <>
      <SEO />
      
      {/* Auto-Scrolling Sale Banner */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-orange-500 via-teal-500 to-orange-500 py-2 overflow-hidden">
        <div className="sale-banner-scroll flex whitespace-nowrap">
          {[...Array(10)].map((_, i) => (
            <span key={i} className="inline-flex items-center mx-8 text-white font-bold text-sm tracking-wide">
              <Sparkles className="w-4 h-4 mr-2" />
              SUMMER TRANSFORMATION SALE ☀️ SAVE UP TO 40% + FINANCING AVAILABLE
              <Sparkles className="w-4 h-4 ml-2" />
            </span>
          ))}
        </div>
      </div>
      
      {/* Hero Section */}
      <section ref={heroRef} className="hero-video-container pt-12">
        <motion.video
          style={{ y }}
          autoPlay
          muted
          loop
          playsInline
          className="hero-video"
        >
          <source src={SITE_CONFIG.heroVideo} type="video/mp4" />
        </motion.video>
        
        <div className="hero-overlay" />
        <div className="hero-gradient-accent" />
        
        <motion.div
          style={{ opacity }}
          className="relative z-10 min-h-screen flex flex-col justify-center px-6 lg:px-8 pt-24"
        >
          <div className="max-w-7xl mx-auto w-full">
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="heading-xl text-white mb-6"
            >
              CREATE THE
              <br />
              <span className="text-gradient-dual">ULTIMATE</span>
              <br />
              WOW FACTOR
            </motion.h1>
            
            <AnimatedSection>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-medium mb-8"
              >
                <Sparkles className="w-4 h-4 text-orange-500" />
                <span className="text-white/70">Licensed & Insured Since 2016</span>
              </motion.span>
            </AnimatedSection>
            
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="body-xl text-white/60 max-w-xl mb-6"
            >
              Premium 3D walls, ivy installations, and custom designs
              that transform ordinary spaces into extraordinary experiences.
            </motion.p>
            
            <motion.a
              href="tel:305-904-8627"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </motion.a>
            
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/contact" data-cursor="GET STARTED">
                <MagneticButton className="btn-primary text-lg">
                  Get Instant Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </MagneticButton>
              </Link>
              <Link to="/transformations" data-cursor="EXPLORE">
                <MagneticButton className="btn-outline">
                  <Play className="w-5 h-5 mr-2" />
                  View Work
                </MagneticButton>
              </Link>
            </motion.div>
          </div>
          
          {/* Scroll indicator - gradient arrow, no bounce */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2"
          >
            <div className="flex flex-col items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-white/40">Scroll</span>
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                className="scroll-arrow-gradient"
              >
                <defs>
                  <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#f97316" />
                    <stop offset="100%" stopColor="#14b8a6" />
                  </linearGradient>
                </defs>
                <path 
                  d="M12 5L12 19M12 19L5 12M12 19L19 12" 
                  stroke="url(#arrowGradient)" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  transform="rotate(180 12 12)"
                />
              </svg>
            </div>
          </motion.div>
        </motion.div>
      </section>
      
      {/* Our Work Section */}
      <section className="py-32 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
              <div>
                <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">What We Do</span>
                <h2 className="heading-lg text-white">Our Work</h2>
              </div>
              <p className="body-lg text-white/50 max-w-lg">
                From stunning 3D walls to lush greenery installations, 
                we transform any space into a work of art.
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((service, i) => (
              <AnimatedSection key={service.id} delay={i * 0.1}>
                <Link to={service.slug} data-cursor="EXPLORE">
                  <motion.div
                    whileHover={{ y: -10 }}
                    className="card-premium group h-80 relative overflow-hidden"
                  >
                    <img
                      src={service.image}
                      alt={service.name}
                      className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-110 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <service.icon className="w-8 h-8 text-orange-500 mb-4 group-hover:text-teal-400 transition-colors" />
                      <h3 className="text-xl font-bold text-white">{service.name}</h3>
                    </div>
                  </motion.div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      {/* Before/After Carousel Section */}
      <BeforeAfterCarousel />
      
      {/* Process Section */}
      <section className="py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-20">
              <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">How It Works</span>
              <h2 className="heading-lg text-white">Simple Process</h2>
            </div>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-16">
            {[
              { num: "01", title: "Share Your Vision", desc: "Send us photos and measurements of your space", icon: Send },
              { num: "02", title: "Get Your Quote", desc: "Receive a detailed proposal within 24 hours", icon: Sparkles },
              { num: "03", title: "WOW Factor", desc: "Watch your space transform into a masterpiece", icon: Eye },
            ].map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div className="text-center group">
                  <div className="relative mb-8">
                    <span className="text-[120px] font-black text-white/5 leading-none block">{step.num}</span>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-teal-400 flex items-center justify-center group-hover:scale-110 transition-transform glow-dual">
                        <step.icon className="w-8 h-8 text-black" />
                      </div>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-white/50">{step.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          
          <AnimatedSection delay={0.5}>
            <div className="text-center mt-16">
              <Link to="/our-process" className="btn-secondary" data-cursor="LEARN MORE">
                Learn Our Process
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      <ReviewsSection />
      <InstagramFeed />
      <GallerySection />
      
      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 via-transparent to-teal-500/20" />
        <div className="absolute inset-0 bg-grid opacity-30" />
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <AnimatedSection>
            <h2 className="heading-lg text-white mb-6">
              Ready for Your
              <br />
              <span className="text-gradient-dual">WOW Factor?</span>
            </h2>
            <p className="body-xl text-white/60 mb-12 max-w-2xl mx-auto">
              Join hundreds of satisfied customers across South Florida.
              Get your free quote today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-cursor="START NOW">
                <MagneticButton className="btn-primary text-lg">
                  Get Instant Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </MagneticButton>
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} data-cursor="CALL US">
                <MagneticButton className="btn-outline">
                  <Phone className="w-5 h-5 mr-2" />
                  {SITE_CONFIG.phone}
                </MagneticButton>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

// ============ ABOUT PAGE ============
const AboutPage = () => (
  <>
    <SEO title="About Us" />
    
    {/* Image Hero Section */}
    <section className="relative min-h-[60vh] overflow-hidden">
      <img
        src="https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/j2fx9lbl_banner-home-page-new.png"
        alt="CSD3DWALLS Team"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/70 via-[#0a0a0a]/50 to-[#0a0a0a]" />
      <div className="relative z-10 min-h-[60vh] flex items-center justify-center">
        <AnimatedSection>
          <div className="text-center px-6">
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Our Story</span>
            <h1 className="heading-lg text-white">About CSD3DWALLS</h1>
          </div>
        </AnimatedSection>
      </div>
    </section>
    
    {/* Content Section */}
    <section className="py-20 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <div className="space-y-6 text-white/70 text-lg leading-relaxed">
              <p>
                We are <span className="text-orange-500 font-semibold">CSD3DWALLS!</span> A team of artists and designers led by husband-and-wife team Victoria & Emmanuel Soler, creating wow factors on the daily and bringing your walls to life!
              </p>
              <p>
                Since 2016, we have been transforming spaces whether commercial or residential, bringing your vision to life. No matter the size or scope, our walls are a definite Game Changer!
              </p>
              <p>
                We believe any wall or space is an opportunity to add great value to your home or business, because first impressions are everything!
              </p>
              <p>
                We are blessed to do what we do daily, whether you're opening the doors to your home or businesses we Thank you! We would not be here without the #CSDFAM! Thank you for being a part of our journey and our lives!
              </p>
              <p className="text-orange-500 font-semibold">
                Sincerely,<br />
                The Entire Team at CSD3DWALLS
              </p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 mt-8" data-cursor="START">
              Start Your Project <ArrowRight className="w-4 h-4" />
            </Link>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/4lmigc14_banner-home-page-new.png" 
                alt="Coastal Soul Designs Showroom" 
                className="rounded-2xl w-full" 
              />
              <div className="absolute -bottom-6 -left-6 px-6 py-4 bg-gradient-to-r from-orange-500 to-teal-400 rounded-xl font-bold text-black text-lg">
                Licensed & Insured Since 2016
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
    <GallerySection />
  </>
);

// ============ CONTACT PAGE ============
const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot field - if filled, it's a bot
    const formElement = e.target;
    const botField = formElement.querySelector('input[name="bot-field"]');
    
    if (botField && botField.value) {
      // Silently fail for bots - don't show error
      setStatus("success");
      return;
    }
    
    // Validate that required fields are not blank (trim whitespace)
    const trimmedName = form.name.trim();
    const trimmedEmail = form.email.trim();
    const trimmedPhone = form.phone.trim();
    const trimmedMessage = form.message.trim();
    
    if (!trimmedName || !trimmedEmail || !trimmedPhone || !trimmedMessage) {
      setStatus("validation_error");
      return;
    }
    
    // Minimum length validation to prevent short spam entries
    if (trimmedName.length < 2 || trimmedMessage.length < 10) {
      setStatus("validation_error");
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setStatus("invalid_email");
      return;
    }
    
    // Phone validation - must have at least 10 digits
    const phoneDigits = trimmedPhone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setStatus("invalid_phone");
      return;
    }
    
    setLoading(true);
    try {
      // Submit to serverless function for validation, then to Netlify Forms
      const formData = new URLSearchParams();
      formData.append("name", trimmedName);
      formData.append("email", trimmedEmail);
      formData.append("phone", trimmedPhone);
      formData.append("subject", form.subject.trim());
      formData.append("message", trimmedMessage);
      
      const response = await fetch("/.netlify/functions/submit-form", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString()
      });
      
      if (response.ok) {
        setStatus("success");
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        console.error('Form submission failed:', response.status);
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
    setLoading(false);
  };
  
  return (
    <>
      <SEO title="Contact Us" />
      <section className="pt-40 pb-32 bg-[#0a0a0a] min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-sm font-semibold uppercase tracking-widest text-teal-400 mb-4 block">Get In Touch</span>
              <h1 className="heading-lg text-white">Let&apos;s Create Magic</h1>
            </div>
          </AnimatedSection>
          
          <div className="grid lg:grid-cols-2 gap-16">
            <AnimatedSection>
              <div className="space-y-8">
                <a href={`tel:${SITE_CONFIG.phone}`} className="card-premium p-8 block hover:border-orange-500/50 transition-colors cursor-pointer">
                  <Phone className="w-8 h-8 text-orange-500 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Call Us</h3>
                  <span className="text-white/60 hover:text-orange-500 transition-colors text-lg">
                    {SITE_CONFIG.phone}
                  </span>
                </a>
                
                <a href={`mailto:${SITE_CONFIG.email}`} className="card-premium p-8 block hover:border-teal-400/50 transition-colors cursor-pointer">
                  <Mail className="w-8 h-8 text-teal-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Email</h3>
                  <span className="text-white/60 hover:text-teal-400 transition-colors">
                    {SITE_CONFIG.email}
                  </span>
                </a>
                
                <div className="card-premium p-8">
                  <MapPin className="w-8 h-8 text-orange-500 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Visit Us</h3>
                  <p className="text-white/60">{SITE_CONFIG.address}</p>
                  <p className="text-white/40 text-sm mt-2">Mon-Fri: 9am-5pm | Sat: By Appt</p>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <form 
                onSubmit={handleSubmit} 
                className="card-premium p-8 space-y-6"
              >
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="col-span-2 sm:col-span-1 px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-orange-500 focus:outline-none transition-colors"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="col-span-2 sm:col-span-1 px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-orange-500 focus:outline-none transition-colors"
                  />
                </div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-orange-500 focus:outline-none transition-colors"
                />
                <input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-orange-500 focus:outline-none transition-colors"
                />
                <textarea
                  name="message"
                  placeholder="Tell us about your project..."
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-orange-500 focus:outline-none transition-colors resize-none"
                />
                
                {status === "validation_error" && (
                  <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400">
                    Please fill in all required fields with valid information (Name: 2+ chars, Message: 10+ chars).
                  </div>
                )}
                {status === "invalid_email" && (
                  <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400">
                    Please enter a valid email address.
                  </div>
                )}
                {status === "invalid_phone" && (
                  <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400">
                    Please enter a valid phone number (at least 10 digits).
                  </div>
                )}
                {status === "success" && (
                  <div className="p-4 bg-teal-500/20 border border-teal-500/50 rounded-xl text-teal-400">
                    Message sent successfully!
                  </div>
                )}
                {status === "error" && (
                  <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400">
                    Something went wrong. Please try again.
                  </div>
                )}
                
                <MagneticButton className="btn-primary w-full" disabled={loading} data-cursor="SEND">
                  {loading ? "Sending..." : "Send Message"}
                  <Send className="w-4 h-4 ml-2" />
                </MagneticButton>
              </form>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </>
  );
};

// ============ PROCESS PAGE ============
const ProcessPage = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  
  // Process step images from user uploads
  const PROCESS_IMAGES = {
    discovery: "https://customer-assets.emergentagent.com/job_16af3f3e-28e8-4c92-987f-3342f522c71e/artifacts/yrsuzurc_Photo%20Jan%2022%202026%2C%2012%2052%2023%20PM.jpg",
    proposal: "https://customer-assets.emergentagent.com/job_16af3f3e-28e8-4c92-987f-3342f522c71e/artifacts/wktr2cyg_Photo%20Jan%2022%202026%2C%2012%2052%2038%20PM.jpg",
    design: "https://customer-assets.emergentagent.com/job_16af3f3e-28e8-4c92-987f-3342f522c71e/artifacts/tjswa39j_Photo%20Jan%2022%202026%2C%2012%2052%2050%20PM.jpg",
    onsite: "https://customer-assets.emergentagent.com/job_16af3f3e-28e8-4c92-987f-3342f522c71e/artifacts/7r9wu8ok_Photo%20Jan%2022%202026%2C%2012%2053%2049%20PM.jpg",
    showoff: "https://customer-assets.emergentagent.com/job_16af3f3e-28e8-4c92-987f-3342f522c71e/artifacts/xf5bxmcp_Photo%20Jan%2022%202026%2C%2012%2054%2052%20PM.jpg"
  };
  
  const steps = [
    { 
      phase: "01", 
      title: "Discovery", 
      desc: "We are just getting to know each other! During this phase we are gathering information about your space or brand to give you a clear idea of the scope of your project. What your interest and look you had in mind for the space. We also gather photos and measurements for the design space.",
      image: PROCESS_IMAGES.discovery 
    },
    { 
      phase: "02", 
      title: "Proposal", 
      desc: "We give you a full proposal as well as an initial estimate based on the size, scope, and complexity of your project!",
      image: PROCESS_IMAGES.proposal 
    },
    { 
      phase: "03", 
      title: "Design", 
      desc: "The fun begins! We start sending you over different design options and ideas for the space. Such as design, color options, lighting, buildouts, the possibilities are ENDLESS! We also advise on timeline as projected for your project to be completed.",
      image: PROCESS_IMAGES.design 
    },
    { 
      phase: "04", 
      title: "On Site", 
      desc: "This is where the magic happens. You meet with The CsdTeam, where we take it from here! Our artists will show up on the agreed date, set up, and get to work! Installs can take anywhere between 1-3 days depending on your specific project need. We will be documenting the experience on social media and appreciate that you do the same! Please tag @CSD3DWALLS so that we can show you some love back.",
      image: PROCESS_IMAGES.onsite 
    },
    { 
      phase: "05", 
      title: "Show It Off!", 
      desc: "This is where the magic happens. You meet with The CsdTeam, where we take it from here! Our artists will show up on the agreed date, set up, and get to work! Installs can take anywhere between 1-3 days depending on your specific project need. We will be documenting the experience on social media and appreciate that you do the same! Please tag @CSD3DWALLS so that we can show you some love back.",
      image: PROCESS_IMAGES.showoff 
    },
  ];
  
  const handlePlayVideo = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };
  
  return (
    <>
      <SEO title="Our Process" />
      
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/j2fx9lbl_banner-home-page-new.png" 
            alt="CSD3DWALLS Process" 
            className="w-full h-full object-cover opacity-40" 
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="text-center py-16">
              <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">How We Work</span>
              <h1 className="heading-lg text-white mb-6">Our Process</h1>
              <p className="body-xl text-white/50 max-w-3xl mx-auto mb-4">
                We know transforming a space can be quite the challenge. Here at CSD3DWALLS, we have made it our mission to create The Ultimate Wow Factor! Whether you come to us with a set design in mind or you are looking for full guidance, we will keep your vision and needs in mind as we work to handcraft the perfect design for your space.
              </p>
              <a 
                href="tel:305-904-8627" 
                className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
              >
                Call Today 305-904-8627 FREE QUOTES
              </a>
              <div>
                <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                  Contact Us Now
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Video Section - Full Width Vertical */}
      <section className="py-16 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-8">
              <h2 className="heading-sm text-white mb-4">Watch Our Transformation Process</h2>
              <p className="text-white/60 max-w-2xl mx-auto">
                See how we bring walls to life! Click play to watch our team in action, transforming ordinary spaces into extraordinary experiences.
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={0.2}>
            <div className="relative rounded-2xl overflow-hidden w-full max-w-2xl mx-auto" style={{ aspectRatio: '9/16' }}>
              <video
                ref={videoRef}
                className="w-full h-full object-contain bg-black"
                poster="https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/j2fx9lbl_banner-home-page-new.png"
                onEnded={() => setIsVideoPlaying(false)}
                playsInline
                preload="metadata"
              >
                <source src="https://customer-assets.emergentagent.com/job_site-revamp-56/artifacts/zxj9u1sb_Video%20Aug%2008%202024%2C%201%2059%2012%20PM.mp4" type="video/mp4" />
              </video>
              
              {/* Play/Pause Overlay */}
              <div 
                className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 cursor-pointer ${isVideoPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'}`}
                onClick={handlePlayVideo}
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-24 h-24 rounded-full bg-gradient-to-r from-orange-500 to-teal-500 flex items-center justify-center shadow-lg"
                >
                  {isVideoPlaying ? (
                    <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="4" width="4" height="16" />
                      <rect x="14" y="4" width="4" height="16" />
                    </svg>
                  ) : (
                    <Play className="w-10 h-10 text-white ml-1" />
                  )}
                </motion.div>
              </div>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={0.3}>
            <div className="flex flex-wrap justify-center gap-8 mt-8">
              <div className="flex items-center gap-3 text-white/70">
                <span className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-sm">✓</span>
                Professional installation
              </div>
              <div className="flex items-center gap-3 text-white/70">
                <span className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-sm">✓</span>
                Attention to detail
              </div>
              <div className="flex items-center gap-3 text-white/70">
                <span className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-sm">✓</span>
                Quality craftsmanship
              </div>
            </div>
            
            <div className="text-center mt-8">
              <button 
                onClick={handlePlayVideo}
                className="btn-primary inline-flex items-center gap-2"
              >
                {isVideoPlaying ? (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="4" width="4" height="16" />
                      <rect x="14" y="4" width="4" height="16" />
                    </svg>
                    Pause Video
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Play with Sound
                  </>
                )}
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Steps */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="heading-md text-white">Our 5-Step Process</h2>
            </div>
          </AnimatedSection>
          
          <div className="space-y-24">
            {steps.map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}>
                  <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                    <div className="relative rounded-2xl overflow-hidden bg-black/20">
                      <img src={step.image} alt={step.title} className="w-full h-auto object-contain" />
                      <div className="absolute top-4 left-4 px-4 py-2 bg-gradient-to-r from-orange-500 to-teal-400 rounded-lg font-bold text-black">
                        Phase {step.phase}
                      </div>
                    </div>
                  </div>
                  <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                    <span className="text-[100px] font-black text-white/5 leading-none block">{step.phase}</span>
                    <h2 className="text-3xl font-bold text-white -mt-16 mb-4">{step.title}</h2>
                    <p className="text-white/60 text-lg leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
          
          <AnimatedSection delay={0.5}>
            <div className="text-center mt-20">
              <Link to="/contact" className="btn-primary" data-cursor="START">
                Start Your Project <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

// ============ TRANSFORMATIONS PAGE ============

const TransformationsPage = () => {
  return (
    <>
      <SEO title="Transformations" />
      <section className="pt-40 pb-16 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center">
              <span className="text-sm font-semibold uppercase tracking-widest text-teal-400 mb-4 block">Portfolio</span>
              <h1 className="heading-lg text-white mb-6">Our Transformations</h1>
              <p className="body-xl text-white/50 max-w-2xl mx-auto">
                Explore our stunning before & after transformations across South Florida
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Before & After Section - Full Images Side by Side */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Before & After</h2>
          </AnimatedSection>
          
          <div className="space-y-20">
            {BEFORE_AFTER_PAIRS.map((pair, i) => (
              <AnimatedSection key={pair.id} delay={i * 0.15}>
                <div className="card-premium p-6 md:p-8">
                  <h3 className="text-2xl font-bold text-white mb-2 text-center">{pair.title}</h3>
                  <p className="text-white/50 text-center mb-8">{pair.desc}</p>
                  
                  {/* Side by Side Images */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Before Image */}
                    <div className="relative">
                      <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-black/80 rounded-lg">
                        <span className="text-white font-bold uppercase tracking-wider">Before</span>
                      </div>
                      <img 
                        src={pair.before} 
                        alt={`${pair.title} - Before`}
                        className="w-full h-auto rounded-xl shadow-lg"
                      />
                    </div>
                    
                    {/* After Image */}
                    <div className="relative">
                      <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-gradient-to-r from-orange-500 to-teal-500 rounded-lg">
                        <span className="text-white font-bold uppercase tracking-wider">After</span>
                      </div>
                      <img 
                        src={pair.after} 
                        alt={`${pair.title} - After`}
                        className="w-full h-auto rounded-xl shadow-lg"
                      />
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Ready for Your Transformation?</h2>
            <p className="text-white/60 mb-8">Get your free quote today and join our list of happy customers!</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="START">
                Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> {SITE_CONFIG.phone}
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

// ============ SERVICE PAGE TEMPLATE ============
const ServicePage = ({ serviceId }) => {
  const service = SERVICES.find(s => s.id === serviceId);
  if (!service) return <div className="text-white text-center py-40">Service not found</div>;
  
  const benefits = [
    { icon: Droplets, text: "Waterproof" },
    { icon: Shield, text: "Durable" },
    { icon: Brush, text: "Easy Maintenance" },
    { icon: Sun, text: "UV Protected" },
  ];
  
  return (
    <>
      <SEO title={service.name} />
      
      {/* Hero */}
      <section className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.image} alt={service.name} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Service</span>
            <h1 className="heading-xl text-white mb-6">{service.name}</h1>
            <p className="body-xl text-white/60 max-w-2xl mb-4">
              Transform your space with our premium {service.name.toLowerCase()} installations.
            </p>
            <a 
              href="tel:305-904-8627" 
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </a>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="GET QUOTE">
                Get Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Benefits */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="card-premium p-6 text-center">
                  <b.icon className="w-10 h-10 text-orange-500 mx-auto mb-4" />
                  <p className="text-white font-medium">{b.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <GallerySection />
      <InstagramFeed />
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Ready to Transform?</h2>
            <Link to="/contact" className="btn-primary" data-cursor="START">
              Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

// ============ EXTERIOR PVC WALL CLADDING PAGE ============
const ExteriorPVCPage = () => {
  const [lightbox, setLightbox] = useState(null);
  
  return (
    <>
      <SEO title="Exterior PVC Wall Cladding" />
      
      {/* Hero with Text Above */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={PVC_EXTERIOR_IMAGES[0].url} alt="Exterior PVC Wall Cladding" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Service</span>
            <h1 className="heading-xl text-white mb-6">Exterior PVC Wall Cladding</h1>
            <p className="body-xl text-white/60 max-w-3xl mb-6">
              PVC (Polyvinyl Chloride) exterior wall cladding is a type of weather-resistant façade covering made from engineered plastic panels designed specifically for outdoor use.
            </p>
            <p className="text-white/60 max-w-3xl mb-6">
              Unlike interior PVC wall panels, exterior grade cladding is formulated to withstand UV exposure, rain, wind, and temperature swings without degrading quickly. It's similar in purpose to traditional vinyl siding used on houses but can come in larger panel formats with more architectural finishes and profiles.
            </p>
            <p className="text-white/60 max-w-3xl mb-4">
              Offering a wide range in size <span className="text-orange-500 font-semibold">up to 16ft walls</span> providing a seamless finish.
            </p>
            <a 
              href="tel:305-904-8627" 
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </a>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="GET QUOTE">
                Get Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Key Benefits */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Key Benefits</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { 
                icon: Droplets, 
                title: "Weather & Moisture Resistance", 
                text: "Exterior PVC cladding creates an impermeable barrier protecting walls from rain, snow, and humidity - it won't rot, rust, or corrode like wood or metal." 
              },
              { 
                icon: Sun, 
                title: "UV & Fade Resistance", 
                text: "Panels can be engineered with UV-stabilizers to slow sun fading and chalking over long exposures." 
              },
              { 
                icon: Brush, 
                title: "Low Maintenance", 
                text: "Beyond occasional washing with water and mild detergent, PVC cladding needs almost no upkeep and does not require painting." 
              },
              { 
                icon: Zap, 
                title: "Energy Efficiency", 
                text: "Some PVC exterior panels add an extra insulating layer, improving thermal performance of the wall system." 
              },
              { 
                icon: Shield, 
                title: "Easy & Fast Installation", 
                text: "Lightweight modular panels with interlocking or track systems reduce labor time relative to brick, stone, or stucco." 
              },
              { 
                icon: Layers, 
                title: "Variety of Styles", 
                text: "Available in multiple profiles, colors, and finishes to achieve any aesthetic you desire." 
              },
            ].map((b, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="card-premium p-6 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <b.icon className="w-6 h-6 text-orange-500" />
                    </div>
                    <h3 className="text-white font-bold">{b.title}</h3>
                  </div>
                  <p className="text-white/60 text-sm">{b.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      {/* Gallery - Simplified for Mobile */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Our Exterior PVC Cladding Projects</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {PVC_EXTERIOR_IMAGES.map((img, i) => (
              <AnimatedSection key={img.id} delay={i * 0.05}>
                <motion.div
                  className="cursor-pointer overflow-hidden rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setLightbox(img)}
                >
                  <img src={img.url} alt={img.title} className="w-full aspect-square object-cover" />
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <InstagramFeed />
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Ready to Transform Your Exterior?</h2>
            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              Get a free quote for your exterior PVC wall cladding project today!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="START">
                Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> {SITE_CONFIG.phone}
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-6 right-6 text-white/50 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ 3D WALLS PAGE ============
const Walls3DPage = () => {
  const [lightbox, setLightbox] = useState(null);
  
  return (
    <>
      <SEO title="3D Walls" />
      
      {/* Hero with Text Above */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={WALLS_3D_IMAGES[0].url} alt="3D Walls" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Service</span>
            <h1 className="heading-xl text-white mb-6">3D Walls</h1>
            <p className="body-xl text-white/60 max-w-3xl mb-6">
              Our 3D Walls will add a new dimension to your walls and compliment just about any room in the house. They can be installed for residential and commercial use.
            </p>
            <p className="text-white/60 max-w-3xl mb-6">
              All our material is waterproof and weatherproof, can be cleaned with a damp cloth. No need to worry about yellowing or moisture peeling, and works great in indoor or exterior spaces. This makes excellent feature walls in any home or business. Adding the perfect amount of dimension and pop of color.
            </p>
            <p className="text-white/60 max-w-3xl mb-4">
              Coastal Soul Designs is the #1 rated 3D wall panel installation company in Miami Dade, Broward and Palm Beach County. We have helped hundreds of happy customers transform their home or business in Fort Lauderdale, Boca Raton, Miami, Plantation, Hollywood and West Palm Beach. If you are looking for the lowest prices and the best quality products, then don't hesitate to call CSD. We offer instant quotes after you send us pictures of the space and measurements.
            </p>
            <a 
              href="tel:305-904-8627" 
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </a>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="GET QUOTE">
                Contact Us Now <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Featured Image */}
      <section className="py-12 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <img src={WALLS_3D_IMAGES[1].url} alt="3D Wall Installation" className="w-full rounded-2xl shadow-2xl" />
          </AnimatedSection>
        </div>
      </section>
      
      {/* Advantages */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Advantages of Our 3D Walls</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Droplets, title: "Waterproof & Weatherproof", text: "All our material is waterproof and weatherproof" },
              { icon: Shield, title: "No Maintenance", text: "Our walls have no maintenance, upkeep, or commitment for years to come" },
              { icon: Brush, title: "Problem Wall Solutions", text: "Great solutions to ugly, stubborn problematic walls, ceilings or any surface" },
              { icon: Sun, title: "Lightweight & Fire Resistant", text: "Our wall panels are lightweight and fire resistant" },
            ].map((b, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="card-premium p-6 h-full">
                  <b.icon className="w-10 h-10 text-orange-500 mb-4" />
                  <h3 className="text-white font-bold mb-2">{b.title}</h3>
                  <p className="text-white/60 text-sm">{b.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      {/* Design Solutions */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <h2 className="heading-sm text-white mb-6">3D Wall Design Solutions</h2>
              <p className="text-white/60 mb-6">
                Our walls are great solutions to ugly, stubborn problematic walls, ceilings or any surface that needs covering. Spaces such as:
              </p>
              <div className="grid grid-cols-2 gap-4 text-white/70 mb-6">
                <ul className="space-y-2">
                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> Living Room</li>
                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> Bedrooms</li>
                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> TV Walls</li>
                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> Office</li>
                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> Restaurant</li>
                </ul>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> Lobbies</li>
                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> Reception Desk Areas</li>
                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> Cafes</li>
                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> BBQ Areas</li>
                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> Nightclubs</li>
                </ul>
              </div>
              <p className="text-white/60">
                Providing a wide variety of 3D designs and <span className="text-orange-500 font-semibold">over 10,000 color options</span> for the possibilities are endless.
              </p>
              <Link to="/contact" className="btn-primary mt-6 inline-flex" data-cursor="CONTACT">
                Contact Us Now <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <img src={WALLS_3D_IMAGES[2].url} alt="3D Wall Design" className="rounded-2xl shadow-xl" />
            </AnimatedSection>
          </div>
        </div>
      </section>
      
      {/* Gallery - Simplified for Mobile */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Our 3D Wall Projects</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {WALLS_3D_IMAGES.map((img, i) => (
              <AnimatedSection key={img.id} delay={i * 0.05}>
                <motion.div
                  className="cursor-pointer overflow-hidden rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setLightbox(img)}
                >
                  <img src={img.url} alt={img.title} className="w-full aspect-square object-cover" />
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <InstagramFeed />
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Ready to Transform Your Space?</h2>
            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              Coastal Soul Designs is the #1 rated 3D wall panel installation company in Miami Dade, Broward and Palm Beach County. Get your instant quote today!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="START">
                Contact Us Now <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> {SITE_CONFIG.phone}
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-6 right-6 text-white/50 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ ARTIFICIAL GREENERY PAGE ============
const ArtificialGreeneryPage = () => {
  const [lightbox, setLightbox] = useState(null);
  
  return (
    <>
      <SEO title="Artificial Greenery Walls" />
      
      {/* Hero with Text Above */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={GREENERY_IMAGES[0].url} alt="Artificial Greenery" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Service</span>
            <h1 className="heading-xl text-white mb-6">Artificial Greenery Walls</h1>
            <p className="body-xl text-white/60 max-w-3xl mb-4">
              Our Artificial Ivy Walls are perfect for adding privacy to an outdoor patio area, aesthetically enhance your area with a realistic look to beautify and transform your walls, fence, patio, garden, yard, walkways, interior and exterior.
            </p>
            <a 
              href="tel:305-904-8627" 
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </a>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="GET QUOTE">
                Get Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Benefits Section - Text First */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <AnimatedSection>
              <h2 className="heading-sm text-white mb-6">Benefits of Our Ivy Walls</h2>
              <ul className="space-y-4 text-white/70">
                <li>• Our Artificial Ivy Walls can transform any home or commercial space.</li>
                <li>• Our walls have no upkeep or maintenance, creating greenery all year around, for years to come.</li>
                <li>• A cost-efficient way to beautify and aesthetically increase the value of your property.</li>
                <li>• Creates a natural and calming environment.</li>
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <h2 className="heading-sm text-white mb-6">Advantages of Our Ivy Walls</h2>
              <div className="grid grid-cols-2 gap-4">
                {["UV Protected", "Inexpensive", "Water Resistant", "Eco-friendly", "Lightweight yet Strong", "Soundproof Qualities"].map((adv, i) => (
                  <div key={i} className="card-premium p-4 text-center">
                    <p className="text-white font-medium text-sm">{adv}</p>
                  </div>
                ))}
              </div>
              <p className="text-white/60 mt-6 text-sm">
                Made of lightweight yet super strong high-density polyethylene that is soft to the touch. They have many soundproof qualities, by reducing echoes when applied to the walls.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>
      
      {/* Applications */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Interior Decorative Applications</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {["Balcony", "Living Room", "Trellis", "Study Room", "Terrace", "Bathroom", "Office Work Areas", "Hotels", "Restaurants", "Lobbies", "Weddings", "Reception Desk"].map((app, i) => (
              <AnimatedSection key={i} delay={i * 0.05}>
                <div className="card-premium p-4 text-center">
                  <p className="text-white font-medium text-sm">{app}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      {/* Gallery - After Text */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Our Greenery Projects</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {GREENERY_IMAGES.map((img, i) => (
              <AnimatedSection key={img.id} delay={i * 0.05}>
                <motion.div
                  className="cursor-pointer overflow-hidden rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setLightbox(img)}
                >
                  <img src={img.url} alt={img.title} className="w-full aspect-square object-cover" />
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <InstagramFeed />
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Ready to Transform?</h2>
            <p className="text-white/60 mb-8">Coastal Soul Designs is the leading faux ivy privacy fence installation company in all of Miami Dade, West Palm Beach and Broward County.</p>
            <Link to="/contact" className="btn-primary" data-cursor="START">
              Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-6 right-6 text-white/50 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ WAINSCOTING PAGE ============
const WainscotingPage = () => {
  const [lightbox, setLightbox] = useState(null);
  
  return (
    <>
      <SEO title="Wainscoting Walls" />
      
      {/* Hero with Text */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={WAINSCOTING_IMAGES[0].url} alt="Wainscoting Walls" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Service</span>
            <h1 className="heading-xl text-white mb-6">Wainscoting Walls</h1>
            <p className="body-xl text-white/60 max-w-3xl mb-4">
              Whether your home is a new build or comes with 100 years of character, endless design option ideas can help bring out the best in your space. Wainscoting is a decorative wall trim that's centuries old and has never gone out of style.
            </p>
            <a 
              href="tel:305-904-8627" 
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </a>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="GET QUOTE">
                Get Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Benefits - Text First */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="heading-sm text-white mb-6">Versatile Design Options</h2>
              <p className="text-white/60 mb-8">
                Dress up your plain-Jane walls with stylish trim that also guards against scuffs and scratches. With almost no upkeep, top it off with over 10,000 color options where you can wipe stray marks with a sponge and soapy water.
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={0.2}>
            <h3 className="text-xl font-bold text-white text-center mb-8">Most Popular Areas</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {["Entryway Walls", "Stairs & Hallways", "Dining Area", "Family Rooms", "Kids Rooms", "And Much More!"].map((area, i) => (
                <div key={i} className="card-premium p-4 text-center">
                  <p className="text-white font-medium text-sm">{area}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={0.3}>
            <div className="mt-12 text-center">
              <p className="text-white/60">
                Create a focal point, go bold, enhance architectural features, keep it functional or bring on vintage charm – Wainscoting is a great idea and options are endless.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Gallery - After Text */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Our Wainscoting Projects</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {WAINSCOTING_IMAGES.map((img, i) => (
              <AnimatedSection key={img.id} delay={i * 0.05}>
                <motion.div
                  className="cursor-pointer overflow-hidden rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setLightbox(img)}
                >
                  <img src={img.url} alt={img.title} className="w-full aspect-square object-cover" />
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <InstagramFeed />
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Ready to Transform?</h2>
            <Link to="/contact" className="btn-primary" data-cursor="START">
              Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-6 right-6 text-white/50 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ LED SIGNS PAGE ============
const LEDSignsPage = () => {
  const [lightbox, setLightbox] = useState(null);
  
  return (
    <>
      <SEO title="Custom LED Signs" />
      
      {/* Hero with Text */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={LED_SIGNS_IMAGES[1].url} alt="Custom LED Signs" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Service</span>
            <h1 className="heading-xl text-white mb-6">Custom LED Signs</h1>
            <p className="body-xl text-white/60 max-w-3xl mb-4">
              Since the early 1900s, neon has been used to bring monuments to life with its cool, electric glow. Neon transforms the common place into something amazing, whether it's at your home or business.
            </p>
            <a 
              href="tel:305-904-8627" 
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </a>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="GET QUOTE">
                Get Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Content - Text First */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <AnimatedSection>
              <h2 className="heading-sm text-white mb-6">Why Choose Our LED Signs</h2>
              <p className="text-white/60 mb-6">
                If you're anything like us, you love how neon illuminates a room with its warm, inviting glow, revitalizing the mood, elevating the space, and energizing the vibe.
              </p>
              <p className="text-white/60 mb-6">
                We create our Neon Signs using the latest LED technology and include everything from materials to install. Our LED signs are powered using only 12V power adaptor. They are lightweight, generate no heat, and don't make any noise.
              </p>
              <p className="text-white/60">
                It's made from flexible, long lasting and environmentally friendly LED neon strip with acrylic backing cut to shape. Our neon signs are all hand made to order.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <h2 className="heading-sm text-white mb-6">Features</h2>
              <div className="grid grid-cols-2 gap-4">
                {["Durable", "Bright", "Colorful", "Personalized", "Waterproof", "Ready to Use", "Acrylic Cut to Shape", "Multicolor Options"].map((feature, i) => (
                  <div key={i} className="card-premium p-4 text-center">
                    <p className="text-white font-medium text-sm">{feature}</p>
                  </div>
                ))}
              </div>
              <p className="text-orange-500 font-semibold mt-6 text-center">
                One week turnaround time!
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>
      
      {/* Gallery - After Text */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Our LED Sign Projects</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {LED_SIGNS_IMAGES.map((img, i) => (
              <AnimatedSection key={img.id} delay={i * 0.05}>
                <motion.div
                  className="cursor-pointer overflow-hidden rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setLightbox(img)}
                >
                  <img src={img.url} alt={img.title} className="w-full aspect-square object-cover" />
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <InstagramFeed />
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Let's Light It Up!</h2>
            <p className="text-white/60 mb-8">Creating your own LED neon sign has never been easier. Choose from a variety of fonts, colors and sizes.</p>
            <Link to="/contact" className="btn-primary" data-cursor="START">
              Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-6 right-6 text-white/50 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ REAL WOOD SLAT WALLS PAGE ============
const WoodSlatWallsPage = () => {
  const [lightbox, setLightbox] = useState(null);
  
  return (
    <>
      <SEO title="Real Wood Slat Walls" />
      
      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={WOOD_SLAT_IMAGES[0].url} alt="Real Wood Slat Walls" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Service</span>
            <h1 className="heading-xl text-white mb-6">Real Wood Slat Walls</h1>
            <p className="body-xl text-white/60 max-w-3xl mb-4">
              Looking for High Design Impact? Real wood slat walls are decorative and functional wall treatments made from narrow, evenly spaced wooden battens (slats) mounted vertically or horizontally on a wall.
            </p>
            <a 
              href="tel:305-904-8627" 
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </a>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="GET QUOTE">
                Get Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Content - Text First */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <AnimatedSection>
              <h2 className="heading-sm text-white mb-6">Popular Applications</h2>
              <p className="text-white/60 mb-6">
                They create depth, texture, warmth, and architectural interest in residential or commercial spaces. Offering square or rounded edges and up to 16FT in length.
              </p>
              <div className="grid grid-cols-2 gap-4 text-white/70">
                <div>
                  <h4 className="text-orange-500 font-semibold mb-3">Interior</h4>
                  <ul className="space-y-2">
                    <li>• Living rooms and lounges</li>
                    <li>• Bedrooms (behind headboards)</li>
                    <li>• Hallways and entry walls</li>
                    <li>• TV/media feature walls</li>
                    <li>• Offices and reception areas</li>
                    <li>• Conference rooms (for acoustics)</li>
                  </ul>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <h2 className="heading-sm text-white mb-6">Why Choose Real Wood Slat Walls</h2>
              <div className="space-y-4">
                <h4 className="text-teal-400 font-semibold">Aesthetic Benefits</h4>
                <ul className="space-y-3 text-white/70">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xs mt-0.5">✓</span>
                    <span><strong className="text-white">Natural beauty:</strong> Wood grain adds organic warmth and richness.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xs mt-0.5">✓</span>
                    <span><strong className="text-white">Architectural accent:</strong> Enhances modern, minimalist, and mid-century spaces.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xs mt-0.5">✓</span>
                    <span><strong className="text-white">Customizable:</strong> Species, size, spacing, orientation, and finish can be tailored.</span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
      
      {/* Gallery - After Text */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Our Wood Slat Projects</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {WOOD_SLAT_IMAGES.map((img, i) => (
              <AnimatedSection key={img.id} delay={i * 0.05}>
                <motion.div
                  className="cursor-pointer overflow-hidden rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setLightbox(img)}
                >
                  <img src={img.url} alt={img.title} className="w-full aspect-square object-cover" />
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <InstagramFeed />
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Ready to Transform?</h2>
            <p className="text-white/60 mb-8">Create depth, texture, and warmth in any space with our premium real wood slat walls.</p>
            <Link to="/contact" className="btn-primary" data-cursor="START">
              Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-6 right-6 text-white/50 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ EXTERIOR TONGUE & GROOVE PAGE ============
const TongueGroovePage = () => {
  const [lightbox, setLightbox] = useState(null);
  
  return (
    <>
      <SEO title="Exterior Tongue & Groove Roof & Soffit" />
      
      {/* Hero with Text */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={TONGUE_GROOVE_IMAGES[0].url} alt="Exterior Tongue & Groove" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Service</span>
            <h1 className="heading-xl text-white mb-6">Exterior Tongue & Groove</h1>
            <p className="body-xl text-white/60 max-w-3xl mb-4">
              Tongue and groove (T&G) refers to a profile where each board has a tongue on one edge and a matching groove on the other. These interlock tightly to form a smooth, continuous surface that resists water infiltration and provides strong structural stability.
            </p>
            <a 
              href="tel:305-904-8627" 
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </a>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="GET QUOTE">
                Get Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Benefits - Text First */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Key Benefits</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: "Weather Protection", desc: "Resists water infiltration and provides strong structural stability" },
              { icon: Eye, title: "Premium Look", desc: "Smooth, continuous surface for a high-end aesthetic" },
              { icon: Layers, title: "Material Versatility", desc: "Available in various materials for any style" },
              { icon: Droplets, title: "Completely Waterproof", desc: "Ideal for heavy rain, coastal, or humid areas" },
              { icon: Shield, title: "Immune to Termites", desc: "No rotting or pest damage" },
              { icon: Brush, title: "No Maintenance", desc: "No staining or painting required - lower lifetime costs" },
            ].map((benefit, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="card-premium p-6 text-center h-full">
                  <benefit.icon className="w-10 h-10 text-orange-500 mx-auto mb-4" />
                  <h3 className="text-white font-bold mb-2">{benefit.title}</h3>
                  <p className="text-white/60 text-sm">{benefit.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      {/* Applications - Text */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <AnimatedSection>
              <h2 className="heading-sm text-white mb-6">Applications</h2>
              <p className="text-white/60 mb-6">
                This method is used in siding and cladding for exterior walls, façades, and even ceilings on covered outdoor spaces.
              </p>
              <ul className="space-y-3 text-white/70">
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xs">✓</span>
                  Exterior walls and façades
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xs">✓</span>
                  Covered outdoor ceilings
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xs">✓</span>
                  Patio and porch overhangs
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xs">✓</span>
                  Soffits and roof details
                </li>
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <h2 className="heading-sm text-white mb-6">Compared to Wood</h2>
              <div className="space-y-4 text-white/70">
                <p>
                  Unlike traditional wood, our tongue and groove PVC panels offer significant advantages:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 font-bold text-xs mt-0.5">✓</span>
                    <span><strong className="text-white">No maintenance</strong> - No staining or sealing required</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 font-bold text-xs mt-0.5">✓</span>
                    <span><strong className="text-white">Completely waterproof</strong> - Ideal for Florida's climate</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 font-bold text-xs mt-0.5">✓</span>
                    <span><strong className="text-white">Immune to termites</strong> - No rotting or pest damage</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 font-bold text-xs mt-0.5">✓</span>
                    <span><strong className="text-white">Lower lifetime costs</strong> - No headaches, no upkeep</span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
      
      {/* Gallery - After Text */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Our Tongue & Groove Projects</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {TONGUE_GROOVE_IMAGES.map((img, i) => (
              <AnimatedSection key={img.id} delay={i * 0.05}>
                <motion.div
                  className="cursor-pointer overflow-hidden rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setLightbox(img)}
                >
                  <img src={img.url} alt={img.title} className="w-full aspect-square object-cover" />
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <InstagramFeed />
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Ready to Transform Your Exterior?</h2>
            <p className="text-white/60 mb-8">Premium tongue and groove installation for roofs, soffits, and exterior ceilings.</p>
            <Link to="/contact" className="btn-primary" data-cursor="START">
              Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-6 right-6 text-white/50 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ EXTERIOR ROOF CLADDING PAGE ============
const RoofCladdingPage = () => {
  const [lightbox, setLightbox] = useState(null);
  
  return (
    <>
      <SEO title="Exterior Roof Cladding" />
      
      {/* Hero with Text */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={ROOF_CLADDING_IMAGES[0].url} alt="Exterior Roof Cladding" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Service</span>
            <h1 className="heading-xl text-white mb-6">Exterior Roof Cladding</h1>
            <p className="body-xl text-white/60 max-w-3xl mb-4">
              Exterior PVC roof cladding refers to roof-covering panels or sheets made from polyvinyl chloride (PVC) designed to protect a building from weather while providing a durable, lightweight roofing surface. Up to 16FT panels for a seamless, modern & durable installation.
            </p>
            <a 
              href="tel:305-904-8627" 
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </a>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="GET QUOTE">
                Get Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Key Advantages - Text First */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Key Advantages</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Droplets, title: "Excellent Weather Resistance", desc: "PVC doesn't absorb water, resists moisture, and sheds rain effectively" },
              { icon: Layers, title: "Lightweight & Easy to Handle", desc: "Easier to transport and install than heavy materials like tiles or metal sheets" },
              { icon: Shield, title: "Chemical & Corrosion Resistance", desc: "Tolerates corrosive environments, coastal areas without rusting" },
              { icon: Brush, title: "Low Maintenance", desc: "No painting or sealing required; just periodic cleaning" },
              { icon: Sun, title: "Thermal Reflectivity", desc: "Light-colored PVC reflects solar heat, reducing roof surface temperatures" },
              { icon: Zap, title: "Cost-Effective", desc: "Lower material cost and faster installation than conventional roofing" },
            ].map((advantage, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="card-premium p-6 h-full">
                  <advantage.icon className="w-10 h-10 text-orange-500 mb-4" />
                  <h3 className="text-white font-bold mb-2">{advantage.title}</h3>
                  <p className="text-white/60 text-sm">{advantage.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      {/* Typical Uses */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="heading-sm text-white mb-8">Typical Uses</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  "Commercial Buildings",
                  "Warehouses & Workshops",
                  "Agricultural Structures",
                  "Carports & Covered Walkways",
                  "Garages & Utility Roofs",
                  "Renovation of Existing Roofs"
                ].map((use, i) => (
                  <div key={i} className="card-premium p-4 text-center">
                    <span className="text-teal-400 mr-2">✓</span>
                    <span className="text-white font-medium text-sm">{use}</span>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Gallery - After Text */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Our Roof Cladding Projects</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {ROOF_CLADDING_IMAGES.map((img, i) => (
              <AnimatedSection key={img.id} delay={i * 0.05}>
                <motion.div
                  className="cursor-pointer overflow-hidden rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setLightbox(img)}
                >
                  <img src={img.url} alt={img.title} className="w-full aspect-square object-cover" />
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <InstagramFeed />
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Ready to Transform Your Roof?</h2>
            <p className="text-white/60 mb-8">Premium PVC roof cladding for a seamless, modern & durable installation.</p>
            <Link to="/contact" className="btn-primary" data-cursor="START">
              Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-6 right-6 text-white/50 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ PVC INTERIOR WALL CLADDING PAGE ============
const PVCInteriorPage = () => {
  const [lightbox, setLightbox] = useState(null);
  
  return (
    <>
      <SEO title="Interior PVC Wall Cladding" />
      
      {/* Hero with Text */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={PVC_INTERIOR_IMAGES[0].url} alt="Interior PVC Wall Cladding" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Service</span>
            <h1 className="heading-xl text-white mb-6">Interior PVC Wall Cladding</h1>
            <p className="body-xl text-white/60 max-w-2xl mb-4">
              PVC (Polyvinyl Chloride) interior wall cladding is a lightweight, water-resistant wall covering made from PVC plastic. It's an alternative to traditional materials like ceramic tile, wood paneling, painted drywall or wallpaper.
            </p>
            <a 
              href="tel:305-904-8627" 
              className="inline-block text-xl md:text-2xl font-bold text-orange-500 hover:text-teal-400 transition-colors mb-8"
            >
              Call Today 305-904-8627 FREE QUOTES
            </a>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="GET QUOTE">
                Get Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Key Advantages - Text First */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Key Advantages</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Droplets, title: "Water & Moisture Resistant", desc: "Perfect for areas where moisture resistance matters" },
              { icon: Brush, title: "Easy to Clean", desc: "Simply wipe clean with a damp cloth" },
              { icon: Shield, title: "Low Maintenance", desc: "No painting or special treatments required" },
              { icon: Zap, title: "Cost Effective", desc: "Affordable alternative to traditional materials" },
              { icon: Sun, title: "Durable", desc: "Long-lasting with no yellowing or peeling" },
              { icon: Layers, title: "Variety of Styles", desc: "Multiple colors and surfaces available" },
            ].map((advantage, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="card-premium p-6 h-full">
                  <advantage.icon className="w-10 h-10 text-orange-500 mb-4" />
                  <h3 className="text-white font-bold mb-2">{advantage.title}</h3>
                  <p className="text-white/60 text-sm">{advantage.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      {/* Applications */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="heading-sm text-white mb-8">Perfect For Any Space</h2>
              <p className="text-white/60 mb-8">
                PVC wall cladding comes in a variety of colors and surfaces. Can be used in different spaces of your home or business.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  "Bedroom",
                  "Living Room",
                  "Kitchen",
                  "Laundry Room",
                  "Entryway",
                  "Office Areas",
                  "Interior Roofs",
                  "Commercial Spaces"
                ].map((space, i) => (
                  <div key={i} className="card-premium p-4 text-center">
                    <span className="text-teal-400 mr-2">✓</span>
                    <span className="text-white font-medium text-sm">{space}</span>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Gallery - After Text */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-12">Our Interior Cladding Projects</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {PVC_INTERIOR_IMAGES.map((img, i) => (
              <AnimatedSection key={img.id} delay={i * 0.05}>
                <motion.div
                  className="cursor-pointer overflow-hidden rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setLightbox(img)}
                >
                  <img src={img.url} alt={img.title} className="w-full aspect-square object-cover" />
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      
      <InstagramFeed />
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="heading-md text-white mb-6">Ready to Transform Your Interior?</h2>
            <p className="text-white/60 mb-8">Premium PVC interior wall cladding for a beautiful, durable finish.</p>
            <Link to="/contact" className="btn-primary" data-cursor="START">
              Get Instant Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-6 right-6 text-white/50 hover:text-white" onClick={() => setLightbox(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.title}
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ DISCOUNT POPUP BUTTON ============
const DiscountPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 lg:bottom-6 right-6 z-40 px-4 py-3 rounded-full font-bold text-sm shadow-lg discount-button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 200 }}
        data-cursor="SAVE"
      >
        <span className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Summer Sale!
        </span>
      </motion.button>
      
      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25 }}
              className="relative max-w-md w-full bg-gradient-to-br from-[#0f0f0f] to-[#1a1a1a] rounded-3xl p-8 border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              
              {/* Content */}
              <div className="text-center">
                {/* Sale Image */}
                <div className="mb-6 -mt-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_summer-sale-banner/artifacts/m0c46v3t_7d35a859-d6d6-41ee-8045-1e6a05bad238.png" 
                    alt="Summer Transformation Sale" 
                    className="w-full h-auto rounded-xl"
                  />
                </div>
                
                <h3 className="text-3xl font-bold text-white mb-2">Summer Transformation Sale! ☀️</h3>
                <p className="text-white/60 mb-6">Save up to 40% + financing available on your dream transformation</p>
                
                {/* Discount Code */}
                <div className="bg-white/5 border border-dashed border-orange-500/50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-white/50 mb-2">Use code at checkout:</p>
                  <p className="text-2xl font-bold text-gradient-dual tracking-widest">SUMMER2025</p>
                  <p className="text-orange-500 font-semibold mt-2">Up to 40% OFF + Financing Available!</p>
                </div>
                
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="btn-primary inline-flex items-center gap-2 w-full justify-center"
                >
                  Claim Your Discount
                  <ArrowRight className="w-4 h-4" />
                </Link>
                
                <p className="text-white/30 text-xs mt-4">*Valid for new projects. Limited time offer.</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ SHOP PLACEHOLDER PAGE ============
const ShopPage = () => {
  return (
    <>
      <SEO title="Shop" />
      
      {/* Hero */}
      <section className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-[#0a0a0a] to-teal-500/10" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <AnimatedSection>
            <span className="text-sm font-semibold uppercase tracking-widest text-orange-500 mb-4 block">Coming Soon</span>
            <h1 className="heading-xl text-white mb-6">Shop</h1>
            <p className="body-xl text-white/60 max-w-2xl mx-auto mb-8">
              Our online shop is coming soon! Stay tuned for exclusive products and materials to transform your space.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-primary" data-cursor="CONTACT">
                Contact Us <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <a href={`tel:${SITE_CONFIG.phone}`} className="btn-outline">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </a>
            </div>
          </AnimatedSection>
          
          {/* Notify Section */}
          <AnimatedSection delay={0.2}>
            <div className="mt-16 card-premium p-8 max-w-md mx-auto">
              <h3 className="text-xl font-bold text-white mb-4">Get Notified</h3>
              <p className="text-white/60 mb-6">Follow us on Instagram to be the first to know when our shop launches!</p>
              <a
                href={SITE_CONFIG.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center gap-2"
              >
                <Instagram className="w-5 h-5" />
                Follow @csd3dwalls
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
      
      {/* Services Preview */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="heading-md text-white text-center mb-4">In The Meantime</h2>
            <p className="text-white/60 text-center mb-12">Explore our services and get a free quote for your next project!</p>
          </AnimatedSection>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SERVICES.slice(0, 6).map((service, i) => (
              <AnimatedSection key={service.id} delay={i * 0.1}>
                <Link to={service.slug} className="block">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="relative rounded-xl overflow-hidden aspect-square"
                  >
                    <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white font-bold text-sm md:text-base">{service.name}</h3>
                    </div>
                  </motion.div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

// ============ MAIN APP ============
function App() {
  return (
    <HelmetProvider>
      <div className="App bg-[#0a0a0a] min-h-screen">
        <BrowserRouter>
          <CustomCursor />
          <Header />
          <main className="pb-20 lg:pb-0">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/our-process" element={<ProcessPage />} />
              <Route path="/transformations" element={<TransformationsPage />} />
              <Route path="/shop" element={<ShopPage />} />
              {/* New Service Routes */}
              <Route path="/pvc-interior-wall-cladding" element={<PVCInteriorPage />} />
              <Route path="/exterior-pvc-wall-cladding" element={<ExteriorPVCPage />} />
              <Route path="/exterior-tongue-groove-roof-soffit" element={<TongueGroovePage />} />
              <Route path="/exterior-roof-cladding" element={<RoofCladdingPage />} />
              <Route path="/3d-walls" element={<Walls3DPage />} />
              <Route path="/artificial-greenery-walls" element={<ArtificialGreeneryPage />} />
              <Route path="/real-wood-slat-walls" element={<WoodSlatWallsPage />} />
              <Route path="/wainscoting-walls" element={<WainscotingPage />} />
              <Route path="/custom-led-signs" element={<LEDSignsPage />} />
            </Routes>
          </main>
          <Footer />
          <MobileFooter />
          <DiscountPopup />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
